import AbstractFinanceManage from './AbstractFinanceManage'
import ColumnUtils from '@/utils/ColumnUtils'
import DateUtils from '@/utils/dateUtils'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import { getLoading } from '@/utils/utilBaseElement'
import { getGridSelectedIds, getGridSelected, recordSelectedStatus } from '@/utils/agGrid'
import {
  getJobList,
  getJobList4External,
  queryTotalProfit,
  exportExcel4FinanceManage, updateJobPigeonholeByJobUuid, wkVerifyRollbackPush
} from '@/api/clearing/financeManage'
import { getUserDepartmentGroup, getUserDepartmentTeamNew } from '@/api/user'

import { cacheName, cache } from '@/utils/cacheManager'
import { Job } from '@/class/model/booking/Job'

import * as CommonState from '@/class/cargo/model/parameters/CommonState'
import * as SystemConstants from '@/class/cargo/model/parameters/SystemConstants'
import { Assert } from '@/utils/validate'
import lodash from 'lodash'

import StrUtils from '@/utils/StrUtils'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { Once, stringToBool } from '@/utils/commonUtil'
import { formatNumberToThousands } from '@/utils'
import * as LayersUtil from '@/class/cargo/model/utils/LayersUtil'
import { columnsConvert, valueFormatterConstants } from '@/utils/tableDataExportUtils'
import CustomLableUtils from '@/utils/CustomLabelUtils'
import AdvancedSearchOption from '@/class/cargo/model/parameters/AdvancedSearchOption'
import * as AdvancedSearchConsts from '@/class/cargo/model/utils/AdvancedSearchConsts'
import OperatorConstants from '@/class/cargo/model/parameters/OperatorConstants'
import { JobUtils } from '@/class/cargo/model/utils/JobUtils'
import * as DocumentConstants from '@/class/cargo/model/parameters/DocumentConstants'
import { checkCancelAudit } from '@/api/booking/job'
import * as FinanceInterfaceConstants from '@/class/cargo/model/parameters/FinanceInterfaceConstants'
import { queryChangingChargeList } from '@/api/clearing/changeCharge'
import { getArchivingConfig } from '@/api/sysManage/baseConfig/financialArchivingSetting'
import ArchiveEffectUtils from '@/class/cargo/model/utils/ArchiveEffectUtils'
import convertResToBlob from '@/utils/convertResToBlob'
import LocalStorageUtils from '@/utils/localStorageUtils'
import Store from '@/store'

export default class FinanceManageHP extends AbstractFinanceManage {
  constructor(view) {
    super(view)
    this._fieldSuffix = '_wrapper'
    this.once = new Once(this)
  }

  hasDisplayValue(value) {
    return value !== null && value !== undefined && value !== ''
  }

  setFormattedDisplayFields(data, fields, formatter) {
    for (const [sourceField, targetField] of fields) {
      data[targetField] = formatter(data[sourceField], data)
    }
  }

  getFirstLineDisplay(value) {
    if (!value) {
      return ''
    }
    return String(value).split(/\r\n|\n/)[0]
  }

  getFinanceRowStyle(data) {
    if (!data) {
      return null
    }
    const colorSettings = this.$store.state.settings.colorSettings || LayersUtil.COLOR_STATES
    const colorType = Number(this.$store.state.settings.colorType) === 1
    if (colorType) {
      return JobUtils.financeColor(data)
    }
    if (Object.prototype.hasOwnProperty.call(data, 'blocked') &&
      Object.prototype.hasOwnProperty.call(data, 'specialApproval') &&
      JobUtils.isBlockJob(data)) {
      const blockedColor = colorSettings.find(item => item.colorKey === 'isBlocked')
      return blockedColor ? { background: blockedColor.colorValue } : {}
    }
    const jobState = String(data.jobState) || ''
    const matchedColor = colorSettings.find(item => jobState === item.colorKey)
    return matchedColor ? { background: matchedColor.colorValue } : {}
  }

  prepareRowDisplayFields(data) {
    if (!data) {
      return
    }
    this.setFormattedDisplayFields(data, [
      ['businessDate', '_display_businessDate'],
      ['etd', '_display_etd'],
      ['eta', '_display_eta'],
      ['dateCreated', '_display_dateCreated'],
      ['provisionedDate', '_display_provisionedDate'],
      ['customsDate', '_display_customsDate'],
      ['atd', '_display_atd'],
      ['ata', '_display_ata'],
      ['voucherExportDate', '_display_voucherExportDate'],
      ['preverifyDate', '_display_preverifyDate']
    ], value => DateUtils.formatStandardDate(value))
    this.setFormattedDisplayFields(data, [
      ['destPortReturnTime', '_display_destPortReturnTime'],
      ['destPortDeliveryTime', '_display_destPortDeliveryTime'],
      ['deliveryCompletionDate', '_display_deliveryCompletionDate'],
      ['actTakeTime', '_display_actTakeTime']
    ], value => DateUtils.customFormat(value, DateUtils.STANDARD_MINUTE))
    this.setFormattedDisplayFields(data, [
      ['submissionDate', '_display_submissionDate'],
      ['fcAuditDate', '_display_fcAuditDate'],
      ['preCommitDate', '_display_preCommitDate'],
      ['lockJobDate', '_display_lockJobDate']
    ], value => DateUtils.customFormat(value, DateUtils.STANDARD_DATE_TIME))

    data._display_shipperLine1 = this.getFirstLineDisplay(data.shipperLine1)
    data._display_consigneeLine1 = this.getFirstLineDisplay(data.consigneeLine1)
    data._display_notifyLine1 = this.getFirstLineDisplay(data.notifyLine1)
    data._display_mblShipperLine1 = this.getFirstLineDisplay(data.mblShipperLine1)
    data._display_mblConsigneeLine1 = this.getFirstLineDisplay(data.mblConsigneeLine1)
    data._display_mblNotifyLine1 = this.getFirstLineDisplay(data.mblNotifyLine1)
    data._display_hblShipperLine1 = this.getFirstLineDisplay(data.hblShipperLine1)
    data._display_hblConsigneeLine1 = this.getFirstLineDisplay(data.hblConsigneeLine1)
    data._display_hblNotifyLine1 = this.getFirstLineDisplay(data.hblNotifyLine1)
    data._display_mark = data.mark ? data.mark.trimEnd() : data.mark

    data._display_containerLoadType = ColumnUtils.containerLoadTypeLabelFunction(data, 'containerLoadType')
    data._display_lob = ColumnUtils.jobBussStateLabelFunc(data.lob)
    data._display_customerId = this.hasDisplayValue(data.customerId) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(data.customerId), 'auto_0') : ''
    data._display_cargoType = this.hasDisplayValue(data.cargoType) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_CargoType, 'code', data.cargoType, 'chineseName') : ''
    data._display_customerTypeId = this.hasDisplayValue(data.customerTypeId) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_CustomerType, 'typeId', Number(data.customerTypeId), 'chineseName') : ''
    data._display_cargoSalesId = this.hasDisplayValue(data.cargoSalesId) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.cargoSalesId), 'auto_sales') : ''
    data._display_overseaOp = this.hasDisplayValue(data.overseaOp) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.overseaOp), 'chineseName') : ''
    data._display_siteOp = this.hasDisplayValue(data.siteOp) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.siteOp), 'chineseName') : ''
    data._display_carrierId = carrierLabelFunc(data, data.carrierId)
    data._display_loadingPortCode = portLabelFunc(data, data.loadingPortCode)
    data._display_airDestinationPortCode = airDestinationPortFunction(data, data.airDestinationPortCode)
    data._display_destinationPortCode = destinationPlaceFunction(data, data.destinationPortCode)
    data._display_dischargingPortCode = portLabelFunc(data, data.dischargingPortCode)
    data._display_bookingAgent = this.hasDisplayValue(data.bookingAgent) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(data.bookingAgent), 'auto_1') : ''
    data._display_customJobType = ColumnUtils.resolveCutomJobTypeLabel(TYPE_NAMES.basic_data_CutomJobType, 'code', data.customJobType, 'name', data.lob, data.fba)
    data._display_op = this.hasDisplayValue(data.op) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.op), 'auto_op') : ''
    data._display_sales = this.hasDisplayValue(data.sales) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.sales), 'auto_sales') : ''
    data._display_csr = this.hasDisplayValue(data.csr) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.csr), 'auto_csr') : ''
    data._display_dc = this.hasDisplayValue(data.dc) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.dc), 'chineseName') : ''
    data._display_cargoSource = ColumnUtils.cargoSourceLabelFunction(data.cargoSource)
    data._display_prePortPlanner = this.hasDisplayValue(data.prePortPlanner) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.prePortPlanner), 'chineseName') : ''
    data._display_lastMilePlanner = this.hasDisplayValue(data.lastMilePlanner) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.lastMilePlanner), 'chineseName') : ''
    data._display_prePortApproveStatus = ColumnUtils.priceJobStateLabelFunc(data.prePortPlanner, Number(data.prePortApproveStatus))
    data._display_trunkLineApproveStatus = ColumnUtils.priceJobStateLabelFunc(data.planner, Number(data.trunkLineApproveStatus))
    data._display_lastMileApproveStatus = ColumnUtils.priceJobStateLabelFunc(data.lastMilePlanner, Number(data.lastMileApproveStatus))
    data._display_provisioned = ColumnUtils.booleanLabelFunction(stringToBool(data.provisioned))
    data._display_jobState = ColumnUtils.getJobStateForBlock(data)
    data._display_containerNos = ColumnUtils.getJoinContainerNo(data.containerNos)
    data._display_createdBy = this.hasDisplayValue(data.createdBy) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.createdBy), 'chineseName') : ''
    data._display_containerDesc = ColumnUtils.containerDescLabelFunc(data.containerDesc)
    data._display_designationAgent = this.hasDisplayValue(data.designationAgent) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(data.designationAgent), 'auto_3') : ''
    data._display_paymentTermMBL = ColumnUtils.paymentTermLabelFunc(data.paymentTermMBL)
    data._display_paymentTermHBL = ColumnUtils.paymentTermLabelFunc(data.paymentTermHBL)
    data._display_operateType = ColumnUtils.operateTypeLabelFunc(data, 'operateType')
    data._display_manageAuditStatus = ColumnUtils.manageAuditStatusLabelFunction(data.manageAuditStatus)
    data._display_transportLine = routeLabelFunction(data)
    data._display_receivableInvoiceState = ColumnUtils.receivableInvoiceStateLabelFunction(data.receivableInvoiceState)
    data._display_payableInvoiceState = ColumnUtils.receivableInvoiceStateLabelFunction(data.payableInvoiceState)
    data._display_bookingTerm = (data.lob === 'SE' || data.lob === 'SI') ? ColumnUtils.bookingTermLabelFunction(data, data.bookingTerm) : '-'
    data._display_planner = this.hasDisplayValue(data.planner) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.planner), 'chineseName') : ''
    data._display_receivableChargeInput = ColumnUtils.chargeInputLabelFunction(data.receivableChargeInput)
    data._display_costChargeInput = ColumnUtils.chargeInputLabelFunction(data.costChargeInput)
    data._display_customsBroker = this.hasDisplayValue(data.customsBroker) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(data.customsBroker), 'auto_9') : ''
    data._display_loadingTerm = ColumnUtils.loadingTermNewLabelFunction(data, data.loadingTerm)
    data._display_destinationAgent = this.hasDisplayValue(data.destinationAgent) ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(data.destinationAgent), 'auto_1') : ''
    data._display_userDefinedRoleId1 = this.hasDisplayValue(data.userDefinedRoleId1) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.userDefinedRoleId1), 'chineseName') : ''
    data._display_userDefinedRoleId2 = this.hasDisplayValue(data.userDefinedRoleId2) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.userDefinedRoleId2), 'chineseName') : ''
    data._display_salesOffice = this.hasDisplayValue(data.salesOffice) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Office_All, 'officeId', Number(data.salesOffice), 'chineseName') : ''
    data._display_opOffice = this.hasDisplayValue(data.opOffice) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Office_All, 'officeId', Number(data.opOffice), 'chineseName') : ''
    data._display_salesDepartment = this.hasDisplayValue(data.salesDepartment) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Department_All, 'departmentId', Number(data.salesDepartment), 'chineseName') : ''
    data._display_trustJobState = this.hasDisplayValue(data.trustJobState) ? ColumnUtils.getJobStateCN(Number(data.trustJobState)) : ''
    data._display_relatedJobState = this.hasDisplayValue(data.relatedJobState) ? ColumnUtils.getJobStateCN(Number(data.relatedJobState)) : ''
    data._display_preCommitUser = this.hasDisplayValue(data.preCommitUser) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.preCommitUser), 'chineseName') : ''
    data._display_commitUser = this.hasDisplayValue(data.commitUser) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.commitUser), 'chineseName') : ''
    data._display_fcAuditorUser = this.hasDisplayValue(data.fcAuditorUser) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.fcAuditorUser), 'chineseName') : ''
    data._display_lockJobUser = this.hasDisplayValue(data.lockJobUser) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.lockJobUser), 'chineseName') : ''
    data._display_jobProfitShare = ColumnUtils.booleanLabelFunction(data.jobProfitShare)
    data._display_preverifier = this.hasDisplayValue(data.preverifier) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(data.preverifier), 'chineseName') : ''
    data._display_csrDepartment = this.hasDisplayValue(data.csrDepartment) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Department_All, 'departmentId', Number(data.csrDepartment), 'chineseName') : ''
    data._display_dcDepartment = this.hasDisplayValue(data.dcDepartment) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Department_All, 'departmentId', Number(data.dcDepartment), 'chineseName') : ''
    data._display_pigeonhole = ColumnUtils.booleanLabelFunction(data.pigeonhole)
    data._display_transitStation = data.transitStation ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_RailwayStation, 'code', data.transitStation, 'auto_railway') : ''
    data._display_destinationStation = data.destinationStation ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_RailwayStation, 'code', data.destinationStation, 'auto_railway') : ''
    data._display_pushStatus = ColumnUtils.jobPushStatusLabelFunc(Number(data.pushStatus))
    data._display_jobJdyxkSyncStatus = ColumnUtils.jobJdyxkSyncStatusLabelFunc(data.jobJdyxkSyncStatus)
    data._display_pushExternalInterfaceStatus = ColumnUtils.zyInterfacePushStatusLabelFunc(data.pushExternalInterfaceStatus)
    data._display_hmPushExternalInterfaceStatus = ColumnUtils.zyInterfacePushStatusLabelFunc(data.hmPushExternalInterfaceStatus)
    data._display_saleProductCode = this.hasDisplayValue(data.saleProductCode) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_AllowSaleProduct, 'productCode', data.saleProductCode, 'chineseName') : ''
    data._display_fullOrInside = ColumnUtils.fullOrInsideLabelFun(data.fullOrInside)
    data._display_salesProvision = ColumnUtils.booleanLabelFunction(data.salesProvision)
    data._display_opTicketProvision = ColumnUtils.booleanLabelFunction(data.opTicketProvision)
    data._display_businessProvision = ColumnUtils.booleanLabelFunction(data.businessProvision)
    data._display_csProvision = ColumnUtils.booleanLabelFunction(data.csProvision)
    data._display_opProvision = ColumnUtils.booleanLabelFunction(data.opProvision)
    data._display_customsProvision = ColumnUtils.booleanLabelFunction(data.customsProvision)
    data._display_fba = ColumnUtils.booleanLabelFunction(stringToBool(data.fba))
    data._display_destinationDistribution = ColumnUtils.multipleFbaDestinationDistribution(TYPE_NAMES.local_cache_FbaJobConstants$DESTINATION_DISTRIBUTION_LIST, 'code', data.destinationDistribution)
    data._display_remoteWarehouse = ColumnUtils.booleanLabelFunction(stringToBool(data.remoteWarehouse))
    data._display_trustJobStateFormatted = this.hasDisplayValue(data.trustJobState) ? ColumnUtils.getJobStateCN(Number(data.trustJobState)) : ''
    data._display_relatedJobStateFormatted = this.hasDisplayValue(data.relatedJobState) ? ColumnUtils.getJobStateCN(Number(data.relatedJobState)) : ''
    data._display_actTakeTimeFormatted = ColumnUtils.dateTimeLabelFunc(data.actTakeTime)
    data._display_pigeonholeDate = ColumnUtils.dateLabelFunc(data.pigeonholeDate)
    data._display_cargoCode = this.hasDisplayValue(data.cargoCode) ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_CargoInformation, 'code', data.cargoCode, 'chineseName') : ''
    data._display_receivableChargeLockedStatus = ColumnUtils.chargeLockedStatusLabelFunc(Number(data.receivableChargeLockedStatus))
    data._display_payableChargeLockedStatus = ColumnUtils.chargeLockedStatusLabelFunc(Number(data.payableChargeLockedStatus))
    data._display_allocation = ColumnUtils.booleanLabelFunction(stringToBool(data.allocation))
    data._display_peer = ColumnUtils.booleanLabelFunction(data.peer)
    data._display_typeMBL = ColumnUtils.typeMBLLabelFunc(Number(data.typeMBL))
    data._display_typeHBL = ColumnUtils.typeHBLLabelFunc(Number(data.typeHBL))
    data._display_shutOut = ColumnUtils.booleanLabelFunction(stringToBool(data.shutOut))
    data._display_quantityActual = ColumnUtils.numberColumnLabelFunc(data.quantityActual)
    data._display_weightActual = ColumnUtils.number3ColumnLabelFunc(data.weightActual)
    data._display_cubageActual = ColumnUtils.number3ColumnLabelFunc(data.cubageActual)
    data._display_vessel = data.vessel || ''
    data._display_voyageFlight = data.voyageFlight || ''
    data._display_customerRefNo = data.customerRefNo || ''
    data._display_poNo = data.poNo || ''
    data._display_due = data.due || ''
    data._display_receivable = data.receivable || ''
    data._display_incoTerm = data.incoTerm || ''

    const chargeWeight = Number(data.chargeWeight)
    data._display_chargeWeight = Number.isNaN(chargeWeight) ? '' : Number(ColumnUtils.formatNumber(chargeWeight, 3)).toFixed(3)
    const realProfitValue = this._view.chargeCalculateAmount ? data.realProfit : data.realProfitNoTax
    const incomeValue = this._view.chargeCalculateAmount ? data.income : data.incomeNoTax
    const costValue = this._view.chargeCalculateAmount ? data.cost : data.costNoTax
    data._display_realProfit = formatNumberToThousands(ColumnUtils.realProfit2Func(realProfitValue))
    data._display_grossProfitRate = grossProfitPercentageFunc(data, this._view.chargeCalculateAmount)
    data._display_income = formatNumberToThousands(ColumnUtils.formatNumber(incomeValue, 2, true, true))
    data._display_cost = formatNumberToThousands(ColumnUtils.formatNumber(costValue, 2, true, true))
    data._display_receivableNoVoucher = formatNumberToThousands(ColumnUtils.formatNumber(Number(data.receivableNoVoucher), 2, true, true))
    data._display_payableNoVoucher = formatNumberToThousands(ColumnUtils.formatNumber(Number(data.payableNoVoucher), 2, true, true))
    data._display_receivableVoucher = formatNumberToThousands(ColumnUtils.formatNumber(Number(data.receivableVoucher), 2, true, true))
    data._display_payableVoucher = formatNumberToThousands(ColumnUtils.formatNumber(Number(data.payableVoucher), 2, true, true))
    data._display_rowStyle = this.getFinanceRowStyle(data)
    data._display_jobNoHtml = `${data.jobNo || ''}<i class="el-icon-search" onclick="${this._view.documentManage.getPath('showTemplateDialog')}('${data.uuid}')" style="color:#46a6ff;cursor:pointer"/>`
  }

  prepareListDisplayFields(rowData) {
    if (!rowData) {
      return
    }
    for (const data of rowData) {
      this.prepareRowDisplayFields(data)
    }
  }

  optimizeColumnDefsForDisplayFields() {
    if (!Array.isArray(this._view.columnDefs) || this._view.columnDefs.length === 0) {
      return
    }
    const valueGetterFields = {
      containerLoadType: '_display_containerLoadType',
      lob: '_display_lob',
      shipperLine1: '_display_shipperLine1',
      consigneeLine1: '_display_consigneeLine1',
      notifyLine1: '_display_notifyLine1',
      mblShipperLine1: '_display_mblShipperLine1',
      mblConsigneeLine1: '_display_mblConsigneeLine1',
      mblNotifyLine1: '_display_mblNotifyLine1',
      hblShipperLine1: '_display_hblShipperLine1',
      hblConsigneeLine1: '_display_hblConsigneeLine1',
      hblNotifyLine1: '_display_hblNotifyLine1',
      customerId: '_display_customerId',
      cargoType: '_display_cargoType',
      quantityActual: '_display_quantityActual',
      weightActual: '_display_weightActual',
      cubageActual: '_display_cubageActual',
      customerTypeId: '_display_customerTypeId',
      cargoSalesId: '_display_cargoSalesId',
      overseaOp: '_display_overseaOp',
      siteOp: '_display_siteOp',
      carrierId: '_display_carrierId',
      vessel: '_display_vessel',
      voyageFlight: '_display_voyageFlight',
      loadingPortCode: '_display_loadingPortCode',
      airDestinationPortCode: '_display_airDestinationPortCode',
      destinationPortCode: '_display_destinationPortCode',
      dischargingPortCode: '_display_dischargingPortCode',
      etd: '_display_etd',
      eta: '_display_eta',
      businessDate: '_display_businessDate',
      bookingAgent: '_display_bookingAgent',
      dateCreated: '_display_dateCreated',
      voucherExportDate: '_display_voucherExportDate',
      customerRefNo: '_display_customerRefNo',
      poNo: '_display_poNo',
      customJobType: '_display_customJobType',
      op: '_display_op',
      sales: '_display_sales',
      csr: '_display_csr',
      dc: '_display_dc',
      cargoSource: '_display_cargoSource',
      prePortPlanner: '_display_prePortPlanner',
      lastMilePlanner: '_display_lastMilePlanner',
      prePortApproveStatus: '_display_prePortApproveStatus',
      trunkLineApproveStatus: '_display_trunkLineApproveStatus',
      lastMileApproveStatus: '_display_lastMileApproveStatus',
      provisioned: '_display_provisioned',
      jobState: '_display_jobState',
      containerNos: '_display_containerNos',
      provisionedDate: '_display_provisionedDate',
      createdBy: '_display_createdBy',
      containerDesc: '_display_containerDesc',
      designationAgent: '_display_designationAgent',
      paymentTermMBL: '_display_paymentTermMBL',
      paymentTermHBL: '_display_paymentTermHBL',
      operateType: '_display_operateType',
      manageAuditStatus: '_display_manageAuditStatus',
      transportLine: '_display_transportLine',
      destPortReturnTime: '_display_destPortReturnTime',
      destPortDeliveryTime: '_display_destPortDeliveryTime',
      submissionDate: '_display_submissionDate',
      fcAuditDate: '_display_fcAuditDate',
      customsDate: '_display_customsDate',
      deliveryCompletionDate: '_display_deliveryCompletionDate',
      atd: '_display_atd',
      ata: '_display_ata',
      receivableInvoiceState: '_display_receivableInvoiceState',
      payableInvoiceState: '_display_payableInvoiceState',
      due: '_display_due',
      receivable: '_display_receivable',
      bookingTerm: '_display_bookingTerm',
      planner: '_display_planner',
      receivableChargeInput: '_display_receivableChargeInput',
      costChargeInput: '_display_costChargeInput',
      customsBroker: '_display_customsBroker',
      incoTerm: '_display_incoTerm',
      loadingTerm: '_display_loadingTerm',
      destinationAgent: '_display_destinationAgent',
      userDefinedRoleId1: '_display_userDefinedRoleId1',
      userDefinedRoleId2: '_display_userDefinedRoleId2',
      salesOffice: '_display_salesOffice',
      opOffice: '_display_opOffice',
      salesDepartment: '_display_salesDepartment',
      trustJobState: '_display_trustJobState',
      relatedJobState: '_display_relatedJobState',
      preCommitDate: '_display_preCommitDate',
      lockJobDate: '_display_lockJobDate',
      preCommitUser: '_display_preCommitUser',
      commitUser: '_display_commitUser',
      fcAuditorUser: '_display_fcAuditorUser',
      lockJobUser: '_display_lockJobUser',
      jobProfitShare: '_display_jobProfitShare',
      preverifier: '_display_preverifier',
      preverifyDate: '_display_preverifyDate',
      mark: '_display_mark',
      csrDepartment: '_display_csrDepartment',
      dcDepartment: '_display_dcDepartment',
      pigeonhole: '_display_pigeonhole',
      transitStation: '_display_transitStation',
      destinationStation: '_display_destinationStation',
      pushStatus: '_display_pushStatus',
      jobJdyxkSyncStatus: '_display_jobJdyxkSyncStatus',
      pushExternalInterfaceStatus: '_display_pushExternalInterfaceStatus',
      hmPushExternalInterfaceStatus: '_display_hmPushExternalInterfaceStatus',
      saleProductCode: '_display_saleProductCode',
      fullOrInside: '_display_fullOrInside',
      salesProvision: '_display_salesProvision',
      opTicketProvision: '_display_opTicketProvision',
      businessProvision: '_display_businessProvision',
      csProvision: '_display_csProvision',
      opProvision: '_display_opProvision',
      customsProvision: '_display_customsProvision',
      fba: '_display_fba',
      destinationDistribution: '_display_destinationDistribution',
      remoteWarehouse: '_display_remoteWarehouse',
      actTakeTime: '_display_actTakeTimeFormatted',
      pigeonholeDate: '_display_pigeonholeDate',
      cargoCode: '_display_cargoCode',
      receivableChargeLockedStatus: '_display_receivableChargeLockedStatus',
      payableChargeLockedStatus: '_display_payableChargeLockedStatus',
      allocation: '_display_allocation',
      peer: '_display_peer',
      typeMBL: '_display_typeMBL',
      typeHBL: '_display_typeHBL',
      shutOut: '_display_shutOut'
    }
    const cellRendererFields = {
      chargeWeight: '_display_chargeWeight',
      realProfit: '_display_realProfit',
      grossProfitRate: '_display_grossProfitRate',
      income: '_display_income',
      cost: '_display_cost',
      receivableNoVoucher: '_display_receivableNoVoucher',
      payableNoVoucher: '_display_payableNoVoucher',
      receivableVoucher: '_display_receivableVoucher',
      payableVoucher: '_display_payableVoucher'
    }
    const getDisplayValue = displayField => params => {
      return params && params.data ? params.data[displayField] || '' : ''
    }
    this._view.columnDefs.forEach(column => {
      const colId = column.colId || column.field
      if (valueGetterFields[colId]) {
        const getter = getDisplayValue(valueGetterFields[colId])
        column.valueGetter = getter
        if (column.filter !== false) {
          column.filterValueGetter = getter
        }
        delete column.valueFormatter
        if (!column.cellRendererFramework) {
          delete column.cellRenderer
        }
      } else if (cellRendererFields[colId]) {
        column.cellRenderer = getDisplayValue(cellRendererFields[colId])
      }
    })
  }

  updateResultProvisioned() {
    this.doSearchWithProfitAndList(true)
    this.$logger.log(' FinanceManageHP updateResultProvisioned')
  }

  async updateBusinessDateClickHandler() {
    await getGridSelectedIds(this._view.gridOptions, 'uuid', this.$t('Message.PleaseSelectTheBusiness'))
    const chooseRows = getGridSelected(this._view.gridOptions)
    for (const job of chooseRows) {
      // 财务归档生效判断
      const warnflag = ArchiveEffectUtils.checkArchiveEffect(job)
      if (warnflag) {
        this.$message.warning(this._view.$t('CargoMsg.NOT_OPERATIONAL'))
        return
      }
    }
    this.financeBtnGroupView.showBusinessDateDialog = true
  }

  async updateBusinessDateConfirmCB(businessDate) {
    const jobKeys = await getGridSelectedIds(this._view.gridOptions, 'uuid', this.$t('Message.PleaseSelectTheBusiness'))
    const loading = getLoading()
    Job.updateBusinessDate(jobKeys, businessDate).then(res => {
      this.financeBtnGroupView.showBusinessDateDialog = false
      this.updateResultProvisioned()
      this.$message.success(this.$t('Clearing.BusinessDateModificationSuccessful'))
    }).catch(res => {
      // todo
    }).finally(() => { loading.close() })
  }

  /**
   * 查看
   * @param needChecked
   * @param detailData
   * @param gridOptions
   * @param treeDataArr
   * @param fromView
   * @returns {Promise<void>}
   * @private
   */
  async showDetailView(needChecked, detailData, gridOptions, treeDataArr, fromView = 'manageList') {
    if (needChecked) {
      const chooseRows = getGridSelected(gridOptions)
      await Assert.isTrue(chooseRows.length === 1, this.$t('CargoMsg.Cargo_HasOneOnly'), 'warning')
      detailData = { ...chooseRows[0] }
    }
    this._view.showDetail = true
    this._view.detailViewSource = fromView
    this.$nextTick(() => {
      this.$refs.financeDetail.showDetail({ ...detailData }, lodash.cloneDeep(treeDataArr))
      if (this._view.routeJobNo) {
        this.$refs.financeDetail.activeTabName = this._view.defActiveTab ? this._view.defActiveTab : 'tabCharges'
        this._view.routeJobNo = null
      }
    })
  }

  // 批量审核
  async batchAuditClickHandler() {
    // await cache.load(cacheName.CONFIG)
    const chooseRows = getGridSelected(this._view.gridOptions)
    await Assert.isTrue(chooseRows.length > 0, this.$t('CargoMsg.Cargo_PleaseSelectBusiness'), 'warning')
    if (this._view.jobAndChargeSubmitterAndAuditorCanNotSame) {
      for (const job of chooseRows) {
        if (job.commitUser === this.$store.getters.userId) {
          this.$message.warning(this.$t('FinanceManage.SelectedDataJobSubmitNotAllowAudit'))
          return
        }
      }
    }
    await cache.load(cacheName.CONFIG)
    this._view.isAuditByJobState = Number(cache.config.get('isAuditByJobState')) === 1
    for (const job of chooseRows) {
      if (this._view.isCooperate(job.cooperateStatus)) {
        this.$message.warning('[' + job.jobNo + ']' + this.$t('CargoMsg.Cargo_IsCooperate'))
        return
      }
      const jobState = CommonState.getStateByCode(SystemConstants.JOB_STATES, job.jobState)
      if (this._view.isAuditByJobState) {
        if (Number(job.jobState) !== 1) {
          const isZh = this.$store.getters.language === 'zh'
          this.$message.warning(this.$t('CargoMsg.Cargo_CAN_NOT_APPROVED', [isZh ? jobState.chineseName : jobState.englishName]))
          return
        }
      } else {
        if (!(Number(job.jobState) === 0 || Number(job.jobState) === 11 || Number(job.jobState) === 1)) {
          const isZh = this.$store.getters.language === 'zh'
          this.$message.warning(this.$t('CargoMsg.Cargo_CAN_NOT_APPROVED', [isZh ? jobState.chineseName : jobState.englishName]))
          return
        }
      }
    }
    this.financeBtnGroupView.editApprovalDate = Number(cache.config.get('isAllowEditApprovalDate')) === 1
    // 判断是否开启验证规则
    const opOffice = (chooseRows && chooseRows.length > 0) ? chooseRows[0].opOffice : 0
    const hasCheckRule = await JobUtils.hasCheckRuleCondition(this.useCheckRule, 13, opOffice)
    if (this.useCheckRule && hasCheckRule) {
      this.financeBtnGroupView.checkDialog.isEditable = false
      this.financeBtnGroupView.showCheckDialog = false
      const callBack = this.showFinanceBtnGroupViewCall
      this.checkingManager(chooseRows, 13, callBack, null, false)
    } else {
      this.financeBtnGroupView.checkDialog.isEditable = true
      this.financeBtnGroupView.showCheckDialog = true
    }
  }
  // 批量取消审核
  async batchCancelAuditClickHandler() {
    const chooseRows = getGridSelected(this._view.gridOptions)
    await Assert.isTrue(chooseRows.length > 0, this.$t('CargoMsg.Cargo_PleaseSelectBusiness'), 'warning')
    if (this._view.jobAndChargeSubmitterAndAuditorCanNotSame) {
      for (const job of chooseRows) {
        if (job.commitUser === this.$store.getters.userId) {
          this.$message.warning(this.$t('FinanceManage.SelectedDataJobSubmitNotAllowCancelAudit'))
          return
        }
      }
    }
    const jobKeys = []
    for (const job of chooseRows) {
      // 财务归档生效判断
      const warnflag = ArchiveEffectUtils.checkArchiveEffect(job)
      if (warnflag) {
        this.$message.warning(this._view.$t('CargoMsg.NOT_OPERATIONAL'))
        return
      }
      if (this._view.isCooperate(job.cooperateStatus)) {
        this.$message.warning('[' + job.jobNo + ']' + this.$t('CargoMsg.Cargo_IsCooperate'))
        return
      }
      const jobState = CommonState.getStateByCode(SystemConstants.JOB_STATES, job.jobState)
      if (Number(job.jobState) !== 2) {
        const isZh = this.$store.getters.language === 'zh'
        this.$message.warning(this.$t('CargoMsg.Cargo_CAN_NOT_CANCEL_APPROVED', [isZh ? jobState.chineseName : jobState.englishName]))
        return
      }
      jobKeys.push(job.uuid)
    }

    //
    const hasGenerateVoucher = await checkCancelAudit({ keys: jobKeys }).then(res => res.data)
    if (hasGenerateVoucher) { // 已生成凭证【当前业务含有已生成凭证的费用】
      const allowCancelApprove = Number(cache.config.get('AllowCancelApprove')) === 1
      const h = this._view.$createElement
      if (allowCancelApprove) {
        const content = h('div', null, [
          h('p', { style: 'overflow-wrap: break-word;' }, this.$t('Message.AreYouSureYouWantToCancelTheApproval')),
          h('p', { style: 'overflow-wrap: break-word;' }, this.$t('Message.BusinessNumberWithGeneratedVouchers') + hasGenerateVoucher)
        ])
        await this.$confirm(content, this.$t('CargoMsg.Cargo_Tips'), { dangerouslyUseHTMLString: true, type: 'warning' })
      } else {
        const msg = this.$t('Message.BusinessNumberWithGeneratedVouchers') + hasGenerateVoucher
        const content = h('div', null, [
          h('p', { style: 'overflow-wrap: break-word;' }, msg),
          h('p', { style: 'overflow-wrap: break-word;' }, this.$t('queryJob.PleaseReselect'))
        ])
        await this.$confirm(content, this.$t('common.tips.prompt'), {
          showCancelButton: false,
          confirmButtonText: this.$t('SeaExport.GotIt'),
          closeOnClickModal: false,
          dangerouslyUseHTMLString: true,
          type: 'warning'
        }).then(() => true).catch(() => false)
        return
      }
    } else {
      await this.$confirm(this.$t('Message.AreYouSureYouWantToCancelTheApproval'), this.$t('CargoMsg.Cargo_Tips'), { type: 'warning' })
    }
    // 维凯取消审核需要先调用财务取消审核接口
    const flag = await this.wkVerifyRollbackPushFunc(chooseRows)
    if (flag) {
      this.loading()
      Job.jobAccountVerifyRollback(jobKeys, this.$store.getters.currentOfficeId, this._view.location).then(res => {
        if (res === 'ok') {
          this.$message.success(this._view.$t('CargoMsg.Cargo_ReviewCancelledSuccessfully'))
        } else if (res === 'repeatClick') {
          this.$message.warning(this._view.$t('Message.RepeatClickJobAccountVerify2'))
          return
        } else {
          this.$message.success(this._view.$t('Message.AsynchronousJobAccountVerify'))
        }
        this.refresh()
      }).finally(() => {
        this.closeLoading()
      })
    }
  }
  async wkVerifyRollbackPushFunc(chooseRows) {
    var flag = true
    if (cache.config.get('ToExoSystem') === 'WK') {
      // 过滤推送状态为“已发送”/“修改待发送”的数据
      const jobUuids = []
      for (const row of chooseRows) {
        if (row.pushStatus > 0) {
          jobUuids.push(row.uuid)
        }
      }
      if (jobUuids.length > 0) {
        flag = false
        const params = {
          dataKeys: jobUuids,
          branchId: this.$store.getters.currentOfficeId,
          userId: this.$store.getters.userId
        }
        this.loading()
        await wkVerifyRollbackPush(params).then(async r => {
          if (r.data) {
            const pushResult = r.data
            // 系统返回的错误提示信息
            const errorMsg = pushResult.errorMsg
            if (errorMsg && errorMsg.length > 0) {
              this.$message.error(errorMsg)
              return flag
            }
            // 接口提示
            const msg = pushResult.resultMsg
            // 失败的uuid集合
            // if (pushResult.failureKeys && pushResult.failureKeys.length > 0) {
            //   msg += '：/n' + pushResult.failureKeys.join('/n')
            // }
            // 成功则调用取消财务审核接口
            if (pushResult.success && pushResult.successKeys && pushResult.successKeys.length > 0) {
              await Job.jobAccountVerifyRollback(pushResult.successKeys, this.$store.getters.currentOfficeId, this._view.location).then(res => {
                if (res === 'ok') {
                  this.$message.success(msg)
                } else if (res === 'repeatClick') {
                  this.$message.warning(this._view.$t('Message.RepeatClickJobAccountVerify2'))
                }
                this.refresh()
              })
            } else {
              this.$message.warning(msg)
            }
          }
        }).finally(() => {
          this.closeLoading()
          this._view.gridOptions.api.refreshCells()
        })
      }
    }
    return flag
  }

  async checkBatchCancelAuditClickHandler(auditDialog) {
    const chooseRows = getGridSelected(this._view.gridOptions)
    await Assert.isTrue(chooseRows.length > 0, this.$t('CargoMsg.Cargo_PleaseSelectBusiness'), 'warning')
    if (this._view.jobAndChargeSubmitterAndAuditorCanNotSame) {
      for (const job of chooseRows) {
        if (job.commitUser === this.$store.getters.userId) {
          this.$message.warning(this.$t('FinanceManage.SelectedDataJobSubmitNotAllowCancelAudit'))
          auditDialog.visible = false
          return
        }
      }
    }
    const jobKeys = []
    for (const job of chooseRows) {
      // 财务归档生效判断
      const warnflag = ArchiveEffectUtils.checkArchiveEffect(job)
      if (warnflag) {
        this.$message.warning(this._view.$t('CargoMsg.NOT_OPERATIONAL'))
        auditDialog.visible = false
        return
      }
      if (this._view.isCooperate(job.cooperateStatus)) {
        this.$message.warning('[' + job.jobNo + ']' + this.$t('CargoMsg.Cargo_IsCooperate'))
        auditDialog.visible = false
        return
      }
      const jobState = CommonState.getStateByCode(SystemConstants.JOB_STATES, job.jobState)
      if (Number(job.jobState) !== 2) {
        const isZh = this.$store.getters.language === 'zh'
        this.$message.warning(this.$t('CargoMsg.Cargo_CAN_NOT_CANCEL_APPROVED', [isZh ? jobState.chineseName : jobState.englishName]))
        auditDialog.visible = false
        return
      }
      jobKeys.push(job.uuid)
    }

    this.loading()
    const hasGenerateVoucher = await checkCancelAudit({ keys: jobKeys }).then(res => res.data).catch(res => {
    }).finally(() => { this.closeLoading() })
    if (hasGenerateVoucher) { // 已生成凭证【当前业务含有已生成凭证的费用】
      const allowCancelApprove = Number(cache.config.get('AllowCancelApprove')) === 1
      const h = this._view.$createElement
      if (allowCancelApprove) {
        const content = h('div', null, [
          h('p', { style: 'overflow-wrap: break-word;' }, this.$t('Message.AreYouSureYouWantToCancelTheApproval')),
          h('p', { style: 'overflow-wrap: break-word;' }, this.$t('Message.BusinessNumberWithGeneratedVouchers') + hasGenerateVoucher)
        ])
        await this.$confirm(content, this.$t('CargoMsg.Cargo_Tips'), { dangerouslyUseHTMLString: true, type: 'warning' })
      } else {
        const msg = this.$t('Message.BusinessNumberWithGeneratedVouchers') + hasGenerateVoucher
        const content = h('div', null, [
          h('p', { style: 'overflow-wrap: break-word;' }, msg),
          h('p', { style: 'overflow-wrap: break-word;' }, this.$t('queryJob.PleaseReselect'))
        ])
        await this.$confirm(content, this.$t('common.tips.prompt'), {
          showCancelButton: false,
          confirmButtonText: this.$t('SeaExport.GotIt'),
          closeOnClickModal: false,
          dangerouslyUseHTMLString: true,
          type: 'warning'
        }).then(() => true).catch(() => false)
      }
    }
    auditDialog.visible = true
  }

  async batchCancelAuditClickRejectReasonHandler(rejectReason) {
    const chooseRows = getGridSelected(this._view.gridOptions)
    const jobKeys = []
    for (const job of chooseRows) {
      jobKeys.push(job.uuid)
    }
    // 维凯取消审核需要先调用财务取消审核接口
    const flag = await this.wkVerifyRollbackPushFunc(chooseRows)
    if (flag) {
      this.loading()
      Job.jobAccountVerifyRollback(jobKeys, this.$store.getters.currentOfficeId, this._view.location, rejectReason).then(res => {
        if (res === 'ok') {
          this.$message.success(this._view.$t('CargoMsg.Cargo_ReviewCancelledSuccessfully'))
        } else if (res === 'repeatClick') {
          this.$message.warning(this._view.$t('Message.RepeatClickJobAccountVerify2'))
          return
        } else {
          this.$message.success(this._view.$t('Message.AsynchronousJobAccountVerify'))
        }
        this.refresh()
      }).finally(() => {
        this.closeLoading()
        this._view.gridOptions.api.refreshCells()
      })
    }
  }
  showFinanceBtnGroupViewCall() {
    return true
  }
  //  验证规则配置公用
  checkingManager(jobs, checkNode, callBack, callBack2, allowPartialCheckPass = false) {
    if (jobs && jobs.length > 0) { // 批量验证
      this.financeBtnGroupView.batchCheckingWindow.checkNode = checkNode
      this.financeBtnGroupView.batchCheckingWindow.jobs = jobs
      this.financeBtnGroupView.batchCheckingWindow.allowPartialCheckPass = allowPartialCheckPass
      this.financeBtnGroupView.batchCheckingWindow.callBack = callBack
      this.financeBtnGroupView.batchCheckingWindow.callBack2 = callBack2
      this.financeBtnGroupView.batchCheckingWindow.visible = true
    }
  }

  async batchAuditConfirmCB({ accountDate, poNo }) {
    const jobKeys = await getGridSelectedIds(this._view.gridOptions, 'uuid')
    await super.batchAuditConfirmCB(jobKeys, accountDate, poNo, false, this._view.location)
  }

  async batchAuditConfirmSuccessCB(res) {
    if (res === 'ok') {
      this.$message.success(this._view.$t('FinanceManage.AccountVerifyBusinessSuccess'))
    } else if (res === 'repeatClick') {
      this.$message.warning(this._view.$t('Message.RepeatClickJobAccountVerify'))
    } else if (res === 'asyncDo') {
      this.$message.success(this._view.$t('Message.AsynchronousJobAccountVerify'))
    } else {
      this.$message.success(this._view.$t('SeaExport.BatchAuditSuccess'))
    }
  }

  async batchAuditConfirmFailCB() {
    this.$message.warning(this.$t('Message.BatchApprovalFailed'))
  }

  // 核对审核日期
  async checkAccountDate(accountDate) {
    let check = false
    const chooseRows = getGridSelected(this._view.gridOptions)
    for (const job of chooseRows) {
      if (new Date(job.businessDate) > accountDate) {
        check = true
        break
      }
    }
    await this.$confirm(check ? this.$t('Message.TheApprovalDateIsEarlierThanTheBusinessDate') : this.$t('CargoMsg.Cargo_SureToReviewTheBills'), this.$t('CargoMsg.Cargo_Tips'), { type: 'warning' })
  }

  updateDefaultMenuState(state) {
    // todo
    this.refresh()
  }

  refresh() {
    // this.doSearch()
    this.doSearchWithProfitAndList()
  }

  // 改单
  async batchCheckChangeConfirmCB(accountDate) {
    // CARGO-23677，批量改单审核按钮失效
    // await super.batchCheckChangeConfirmCB([this.financeBtnGroupView.job.uuid], accountDate)
    let jobKeys = await getGridSelectedIds(this._view.gridOptions, 'uuid')
    if (this._view.showDetail) jobKeys = [this.financeBtnGroupView.job.uuid]
    await super.batchCheckChangeConfirmCB(jobKeys, accountDate, this._view.location)
  }

  // 批量改单
  async batchCheckChangeClickHandler() {
    const list = getGridSelected(this._view.gridOptions)
    await Assert.isTrue(list.length > 0, this.$t('CargoMsg.Cargo_PleaseSelectBusiness'), 'warning')
    if (this._view.jobAndChargeSubmitterAndAuditorCanNotSame) {
      for (const job of list) {
        if (job.changeBy === this.$store.getters.userId) {
          this.$message.warning(this.$t('FinanceManage.SelectedDataChargeByNotAllowCancelAudit'))
          return
        }
      }
    }
    const isZh = this.$store.getters.language === 'zh'
    for (const job of list) {
      if (Number(job.changeState) !== Number(SystemConstants.CHANGE_COMMIT.code)) {
        this.$message.warning(this.$t('CargoMsg.Cargo_CAN_NOT_APPROVED', [isZh ? SystemConstants.CHANGE_COMMIT.chineseName : SystemConstants.CHANGE_COMMIT.englishName]))
        return
      }
    }
    this.financeBtnGroupView.showChangeDialog = true
  }

  // 导出全部
  async exportExcelAllClickHandler(type = '') {
    this._view.searchExportExcel = true
    this._view.search2({ simpleTriggerMode: this._view.simpleTriggerMode, conditions: type === 'detail' ? null : this._view.beforeConditions })
  }

  resultHandler(wrapper) {
    super.resultHandler(wrapper)
  }

  toQueryList(roleMap = null, calcTotalProfit = false, queryMasterDb = false) {
    let loading = null
    const { snapshotConditions: conditions, requires, pageNum, pageSize } = this._view.searchConditions
    const requestBody = {
      conditions,
      requires,
      roleMap,
      pageNum,
      pageSize
    }
    // 通过业务编号/付款申请号点击跳转到财务审核的界面时，查询条件不能追加分公司
    // CARGO-33814 付款申请查看明细，通过放大镜跳转财务审核会查询到与付款申请无关的业务
    if (!this.view.routeJobNo) {
      requestBody.branchId = this._view.accountSetBranchId ? this._view.accountSetBranchId : this.$store.getters.currentOfficeId
    }
    requestBody.appendQuery = this._view.appendQuery
    requestBody.branchCooperate = this._view.branchCooperate
    requestBody.queryMasterDb = queryMasterDb || false
    if (this._view.appendQuery && this._view.rowData) {
      const jobKeys = Array.from(this._view.rowData, p => p.uuid)
      if (jobKeys.length > 0) {
        if (AdvancedSearchOption.hasField('job.uuid', requestBody.conditions)) {
          const option = AdvancedSearchOption.getOption('job.uuid', requestBody.conditions)
          if (option.operator === OperatorConstants.OPERATOR_IN.code) {
            option.value = jobKeys
            AdvancedSearchOption.updateOption(option, requestBody.conditions)
          }
        } else {
          requestBody.conditions.push(new AdvancedSearchOption(null, 'job.uuid', jobKeys, AdvancedSearchConsts.OPERATOR_IN.code).formatObj2Json())
        }
      }
    }
    this.$logger.log('requestBody', requestBody)

    if (this._view.searchExportExcel) {
      if (calcTotalProfit) { // 导出excel的操作，第二次的利润请求 忽略
        this._view.searchExportExcel = false
        return
      }
      loading = getLoading(null, { fullscreen: false })
      // 导出全部，需要 实时获取 用户的 搜索条件
      // { ...requestBody, conditions: this._view.$refs.searchItem.getAdvRequestData() }
      const { columnApi } = this._view.gridOptions
      // 获取显示列
      const allDisplayedColumns = columnApi.getAllDisplayedColumns()
      const columnDefinition = columnsConvert(allDisplayedColumns)
      const constants = valueFormatterConstants()
      exportExcel4FinanceManage({
        ...requestBody,
        columnDefinition: columnDefinition,
        columnLabelSettings: Store.state.settings.partnerLabelSettings,
        constants: constants,
        excelName: this.$t('Clearing.FinancialReviewList')
      }).then((res) => {
        if (res) {
          convertResToBlob(res)
          this.$message.success(this.$t('Clearing.RequestSucceededExporting'))
        }
      }).finally(() => {
        loading.close()
      })
    } else {
      if (calcTotalProfit) {
        queryTotalProfit(requestBody).then(res => {
          super.resultHandlerForTotalProfit(res.data)
        }).finally(() => {
          // 默默的进行 就行了
        })
      } else {
        const selectedStatus = recordSelectedStatus(this._view.gridOptions.api, 'uuid')
        let apiRequest = getJobList
        loading = getLoading(null, { fullscreen: false })
        if (this._view.routeJobNo) {
          apiRequest = getJobList4External
        } else {
          super.clearBottomTotalData()
        }
        apiRequest(requestBody).then(res => {
          let results = res.data.results.list
          this.prepareListDisplayFields(results)
          // 追加查询数据去重
          if (this._view.appendQuery && results) {
            const idSet = this._view.rowData.reduce((acc, v) => {
              acc[v.uuid] = true
              return acc
            }, {})
            const list = results.filter(v => !idSet[v.uuid])
            results = [...this._view.rowData, ...list]
            this._view.rowData = results
            this.resultHandler(res.data)
            this._view.searchConditions.total = results.length
          } else {
            this._view.rowData = results
            this.resultHandler(res.data)
            this._view.searchConditions.total = Number(res.data.results.total)
            this._view.searchConditions.pageCount = res.data.results.pages
          }
          selectedStatus.revert()
        }).finally(() => {
          if (this._view.routeJobNo) {
            const renderedNode = this._view.gridOptions.api.getRenderedNodes()
            if (renderedNode.length !== 0) {
              // renderedNode.find(node => node.data.jobNo === this._view.routeJobNo).setSelected(true)
              renderedNode.find(node => node.data && this._view.routeJobNo.search(node.data.jobNo) !== -1).setSelected(true)
              this.showDetailView(true, null, this._view.gridOptions, this._view.rowData)
            }
          } else if (this._view.$route.params.billPaymentNo) {
            if (this._view.rowData.length > 0) {
              this._view.showDetail = true
              this._view.detailViewSource = 'manageList'
              this.$nextTick(() => {
                this.$refs.financeDetail.showDetail({ ...this._view.rowData[0] }, lodash.cloneDeep(this._view.rowData))
                this.$refs.financeDetail.activeTabName = 'tabCharges'
              })
            }
            delete this._view.$route.params.billPaymentNo
          }
          loading.close()
        })
      }
    }
  }

  async toQueryListWithProfitAndList(roleMap = null, queryMasterDb = false) {
    return Promise.all([this.toQueryList(roleMap, false, queryMasterDb), this.toQueryList(roleMap, true, queryMasterDb)])
  }
  async doSearchWithProfitAndList(queryMasterDb = false) {
    // 是否生成凭证
    if (this._view.hasVoucher !== undefined && this._view.hasVoucher !== -1) {
      await this.doSearch(false, queryMasterDb)
      return
    }
    // 根据配置同步 受控权限
    await cache.load(cacheName.CONFIG)
    this._view.showControlledProfit = Number(cache.config.get('ShowControlledProfit')) === 1
    this._view.allJobByRole = this.$checkPermission(['role_ACPayAuth_allJob'])
    this._view.departmentJobByRole = this.$checkPermission(['role_ACPayAuth_departmentJob'])
    if (this._view.allJobByRole || !this._view.hasCheckRole) {
      await this.doSearch(false, queryMasterDb)
      setTimeout(() => { this.doSearch(true, queryMasterDb) }, 1000)
    } else {
      await this.doSearch(false, queryMasterDb)
    }
  }

  async doSearch(calcTotalProfit = false, queryMasterDb = false) {
    // this._view.rowData = []
    if (this._view.allJobByRole || !this._view.hasCheckRole) {
      const roleMap = {}
      const controlled = this.$checkPermission(['button_Charge_controlled'])
      const costControlled = this.$checkPermission(['button_Charge_costControlled'])
      const showControlledProfit = this._view.showControlledProfit
      roleMap['controlled'] = controlled
      roleMap['costControlled'] = costControlled
      roleMap['showControlledProfit'] = showControlledProfit
      this.toQueryList(roleMap, calcTotalProfit, queryMasterDb)
    } else {
      if (this._view.departmentJobByRole) {
        // 财务审核-同组部门下的用户
        await getUserDepartmentGroup({ category: 'role', shape: 'ACPayAuth', point: 'departmentJob' }).then(async res => {
          const departmentKeys = res && res.data ? Array.from(res.data, p => p.uuid) : []
          if (!departmentKeys || departmentKeys.length <= 0) {
            return
          }
          // 同组部门下的用户
          const resData = await getUserDepartmentTeamNew(departmentKeys, this.$store.getters.currentOfficeId).then(res => {
            return res.data
          })
          this.getUserDepartmentTeamHandler(resData, queryMasterDb)
        }).catch(() => {
          this.$message.warning('查询失败')
        })
      } else {
        super.queryDataRole(false, queryMasterDb)
      }
    }
  }

  // todo-shengsheng  Desc: 预处理表格列，测试
  preConversionColumn(data, fieldSuffix = '') {
    if (data && data.length <= 0) return data
    const columnConversionConfig = {
      [TYPE_NAMES.basic_data_Company]: [
        { field: 'customerId', type: 'cache', sourceKey: 'cmpIdx', targetKey: 'chineseName', computeResult: new Map() },
        {
          field: 'bookingAgent',
          type: 'cache',
          sourceKey: 'cmpIdx',
          targetKey: 'chineseName',
          computeResult: new Map()
        },
        { field: 'carrierId', type: 'cache', sourceKey: 'cmpIdx', targetKey: 'chineseName', computeResult: new Map() },
        {
          field: 'designationAgent',
          type: 'cache',
          sourceKey: 'cmpIdx',
          targetKey: 'chineseName',
          computeResult: new Map()
        }
      ],
      [TYPE_NAMES.basic_data_CustomerType]: [
        {
          field: 'customerTypeId',
          type: 'cache',
          sourceKey: 'typeId',
          targetKey: 'chineseName',
          computeResult: new Map()
        }
      ],
      [TYPE_NAMES.basic_data_CutomJobType]: [
        { field: 'customJobType', type: 'cache', sourceKey: 'code', targetKey: 'name', computeResult: new Map() }
      ],
      [TYPE_NAMES.basic_data_User]: [
        {
          field: 'cargoSalesId',
          type: 'cache',
          sourceKey: 'userId',
          targetKey: 'chineseName',
          computeResult: new Map()
        },
        { field: 'overseaOp', type: 'cache', sourceKey: 'userId', targetKey: 'chineseName', computeResult: new Map() },
        { field: 'op', type: 'cache', sourceKey: 'userId', targetKey: 'chineseName', computeResult: new Map() },
        { field: 'sales', type: 'cache', sourceKey: 'userId', targetKey: 'chineseName', computeResult: new Map() },
        { field: 'dc', type: 'cache', sourceKey: 'userId', targetKey: 'chineseName', computeResult: new Map() },
        { field: 'csr', type: 'cache', sourceKey: 'userId', targetKey: 'chineseName', computeResult: new Map() },
        { field: 'createdBy', type: 'cache', sourceKey: 'userId', targetKey: 'chineseName', computeResult: new Map() },
        { field: 'userDefinedRoleId4', type: 'cache', sourceKey: 'userId', targetKey: 'chineseName', computeResult: new Map() }
      ],
      // 港口分空港海港
      // [TYPE_NAMES.basic_data_Seaport]: [
      //   { field: 'loadingPortCode', type: 'cache', sourceKey: 'code', targetKey: 'englishName', computeResult: new Map() },
      //   { field: 'dischargingPortCode', type: 'cache', sourceKey: 'code', targetKey: 'englishName', computeResult: new Map() }
      // ],
      [TYPE_NAMES.basic_data_PaymentTerm]: [
        { field: 'paymentTermMBL', type: 'cache', sourceKey: 'code', targetKey: 'name', computeResult: new Map() },
        { field: 'paymentTermHBL', type: 'cache', sourceKey: 'code', targetKey: 'name', computeResult: new Map() }
      ],
      // todo-shengsheng  Desc: 其他非缓存，可以通过 自定义的回调渲染函数实现 灵活
      other: [
        {
          field: 'loadingPortCode', type: 'other', cellRenderer: (params) => {
            return portLabelFunc(params.data, params.value)
          }
        },
        {
          field: 'dischargingPortCode', type: 'other', cellRenderer: (params) => {
            return portLabelFunc(params.data, params.value)
          }
        },
        // 目的港
        {
          field: 'airDestinationPortCode', type: 'other', cellRenderer: (params) => {
            return airDestinationPortFunction(params.data, params.value)
          }
        },
        // 交货地
        {
          field: 'destinationPortCode', type: 'other', cellRenderer: (params) => {
            return destinationPlaceFunction(params.data, params.value)
          }
        },
        {
          field: 'chargeWeight', type: 'other', cellRenderer: (params) => {
            return Number(ColumnUtils.formatNumber(params.value, 3)).toFixed(3)
          }
        },
        {
          field: 'cargoSource', cellRenderer: (params) => {
            return ColumnUtils.cargoSourceLabelFunction(params.value)
          }, type: 'other'
        },

        {
          field: 'containerLoadType', cellRenderer: (params) => {
            return ColumnUtils.containerLoadTypeLabelFunction(params.data, 'containerLoadType')
          }, type: 'other'
        },
        {
          field: 'provisioned', cellRenderer: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }, type: 'other'
        },
        {
          field: 'jobState', cellRenderer: (params) => {
            return ColumnUtils.jobStateLabelFunc(params.value)
          }, type: 'other'
        },
        {
          field: 'containerNos', cellRenderer: (params) => {
            return ColumnUtils.getJoinContainerNo(params.value)
          }, type: 'other'
        },
        {
          field: 'realProfit', cellRenderer: (params) => {
            const curValue = this._view.chargeCalculateAmount ? params.value : params.data['realProfitNoTax']
            return ColumnUtils.realProfit2Func(curValue)
          }, type: 'other'
        },
        {
          field: 'costInterest', cellRenderer: (params) => {
            const curValue = params.value == null ? 0 : params.value
            return formatNumberToThousands(ColumnUtils.realProfit2Func(curValue))
          }, type: 'other'
        },
        {
          field: 'interestProfit', cellRenderer: (params) => {
            const curValue = this._view.chargeCalculateAmount ? params.value : params.data['interestProfitNoTax']
            return ColumnUtils.realProfit2Func(curValue)
          }, type: 'other'
        },
        // 毛利率
        {
          field: 'grossProfitRate', cellRenderer: (params) => {
            return grossProfitRateFunc(params.data)
          }, type: 'other'
        },
        // 总收入
        {
          field: 'income', cellRenderer: (params) => {
            const curValue = this._view.chargeCalculateAmount ? params.value : params.data['incomeNoTax']
            return formatNumberToThousands(ColumnUtils.formatNumber(curValue, 2, true, true))
          }, type: 'other'
        },
        // 实际计费重量
        {
          field: 'chargeWeight', cellRenderer: (params) => {
            return Number(ColumnUtils.formatNumber(params.value, 3)).toFixed(3)
          }, type: 'other'
        },
        // 总成本
        {
          field: 'cost', cellRenderer: (params) => {
            const curValue = this._view.chargeCalculateAmount ? params.value : params.data['costNoTax']
            return formatNumberToThousands(ColumnUtils.formatNumber(curValue, 2, true, true))
          }, type: 'other'
        },
        {
          field: 'containerDesc', cellRenderer: (params) => {
            return ColumnUtils.containerDescLabelFunc(params.value)
          }, type: 'other'
        },
        {
          field: 'operateType', cellRenderer: (params) => {
            return ColumnUtils.operateTypeLabelFunc(params.data, 'operateType')
          }, type: 'other'
        },
        {
          field: 'manageAuditStatus', cellRenderer: (params) => {
            return ColumnUtils.manageAuditStatusLabelFunction(params.value)
          }, type: 'other'
        },
        {
          field: 'lob', cellRenderer: (params) => {
            return ColumnUtils.jobBussStateLabelFunc(params.value)
          }, type: 'other'
        },

        {
          field: 'transportLine', cellRenderer: (params) => {
            return routeLabelFunction(params.data)
          }, type: 'other'
        },
        {
          field: 'businessDate', cellRenderer: (params) => {
            return DateUtils.formatStandardDate(params.value)
          }, type: 'other'
        },
        {
          field: 'etd', cellRenderer: (params) => {
            return DateUtils.formatStandardDate(params.value)
          }, type: 'other'
        },
        {
          field: 'eta', cellRenderer: (params) => {
            return DateUtils.formatStandardDate(params.value)
          }, type: 'other'
        },
        {
          field: 'dateCreated', cellRenderer: (params) => {
            return DateUtils.formatStandardDate(params.value)
          }, type: 'other'
        },
        {
          field: 'provisionedDate', cellRenderer: (params) => {
            return DateUtils.formatStandardDate(params.value)
          }, type: 'other'
        },
        {
          field: 'destPortReturnTime', cellRenderer: (params) => {
            return DateUtils.customFormat(params.value, DateUtils.STANDARD_MINUTE)
          }, type: 'other'
        },
        {
          field: 'destPortDeliveryTime', cellRenderer: (params) => {
            return DateUtils.customFormat(params.value, DateUtils.STANDARD_MINUTE)
          }, type: 'other'
        },
        {
          field: 'submissionDate', cellRenderer: (params) => {
            return DateUtils.customFormat(params.value, DateUtils.STANDARD_DATE_TIME)
          }, type: 'other'
        },
        {
          field: 'fcAuditDate', cellRenderer: (params) => {
            return DateUtils.customFormat(params.value, DateUtils.STANDARD_DATE_TIME)
          }, type: 'other'
        },
        {
          field: 'customsDate', cellRenderer: (params) => {
            return DateUtils.formatStandardDate(params.value)
          }, type: 'other'
        }
      ]
    }
    const cacheKeys = Object.keys(columnConversionConfig).filter(key => key !== 'other')

    const allColumnConfigObj = {}
    // 过滤和映射列值的map
    data.forEach(row => {
      cacheKeys.forEach(cacheKey => {
        // 列配置信息数组
        const columnConfigArr = columnConversionConfig[cacheKey]
        columnConfigArr.forEach(columnConfigObj => {
          const { field, computeResult } = columnConfigObj
          // console.log('[row[field]]', field, [row[field]])
          computeResult.set(row[field], '')

          allColumnConfigObj[field] = (columnConfigObj)
        })
      })
    })

    // 结合cache的数据，完善 列值map
    cacheKeys.forEach(cacheKey => {
      const cacheList = ColumnUtils.getBasicDataCacheList(cacheKey)
      const columnConfigArr = columnConversionConfig[cacheKey]

      cacheList.forEach(cache => {
        columnConfigArr.forEach(columnConfigObj => {
          const { sourceKey, targetKey, computeResult } = columnConfigObj
          if (computeResult.has(cache[sourceKey])) {
            computeResult.set(cache[sourceKey], cache[targetKey])
          }
        })
      })
    })

    // 追加other列配置obj
    ;(columnConversionConfig.other || []).forEach(item => {
      allColumnConfigObj[item.field] = item
    })

    // 从列值map中取出相关值，取代rowData的原值，完成转换处理
    const richDatas = data.map(row => {
      for (const field in allColumnConfigObj) {
        const currentColumnConfigObj = allColumnConfigObj[field]
        if (currentColumnConfigObj.type === 'other') {
          // 走自定义渲染函数
          if (currentColumnConfigObj.cellRenderer && typeof currentColumnConfigObj.cellRenderer === 'function') {
            // 自定义函数渲染
            row[field + fieldSuffix] = (currentColumnConfigObj.cellRenderer({ data: row, value: row[field] }) || '')
            // console.log('row[field]', row[field])
          }
        } else {
          const computeResult = currentColumnConfigObj.computeResult
          // 原值替换为计算后的值 todo
          // row[field + '_wrapped'] = computeResult.get(row[field]) || ''
          row[field + fieldSuffix] = computeResult.get(row[field]) || ''
        }
      }
      return row
    })
    // console.log('columnConversionConfig', columnConversionConfig)
    // console.log('richDatas======', richDatas)

    return richDatas
  }

  async beforeMount() {
    super.beforeMount()
    await cache.load(cacheName.CONFIG).then(() => {
      this._view.showControlledProfit = Number(cache.config.get('ShowControlledProfit')) === 1
      this._view.chargeCalculateAmount = Number(cache.config.get('chargeCalculateAmount')) === 1
      this._view.useOrderCenterMode = Number(cache.config.get('useOrderCenterMode')) === 1
    })
  }

  async created() {
    const cxt = this._view
    const currentThis = this
    this._view.documentManage.set('showTemplateDialog', function(uuid) {
      // const data = cxt.rowData.find(item => item.uuid === uuid)
      // todo 需根据不同业务类型，获取模板数据
      cxt.showTemplateDialog()
    })
    // console.log('SeaImportJob.getPropertyNames()', JSON.stringify(new SeaImportJob().getPropertyNames1()))
    this._view.gridOptions = {
      suppressDoubleClickExpand: true,
      rowSelection: 'multiple',
      suppressCellSelection: true,
      suppressRowClickSelection: true,
      rowBuffer: 15,
      suppressRowVirtualisation: false,
      suppressColumnVirtualisation: false,
      suppressAnimationFrame: true,
      suppressRowTransform: true,
      treeData: true,
      groupDefaultExpanded: 2,
      getRowId: params => {
        return params && params.data ? (params.data.uuid || params.data.jobNo) : undefined
      },
      getDataPath: data => {
        return data.path.split(',')
      },
      onRowDataChanged: event => {
        if (this._view && this._view.$refs['searchItem']) {
          this._view.$refs['searchItem'].clickCollapseList()
        }
      },
      onSelectionChanged(event) {
        currentThis.once.excute(currentThis._view.filterChange)
      },
      onRowSelected() {
      },
      onCellDoubleClicked: event => {
        if (!event.data) {
          return
        }
        if (event.colDef.field === 'sopState') {
          LocalStorageUtils.setConfiguration('FinanceSopStatusPopupVisible', true)
          this._view.sopStateDialog.visible = true
          this._view.currentRow = event.data
          event.stopPropagation()
          return
        }
      },
      autoGroupColumnDef: {
        headerName: this.$t('queryJob.businessNumber'),
        field: 'jobNo',
        colId: 'jobNo',
        width: 240,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        cellRendererParams: {
          suppressCount: true,
          suppressDoubleClickExpand: true,
          checkbox: params => {
            return true
          },
          innerRenderer: params => {
            return params.data ? (params.data._display_jobNoHtml || params.value) : params.value
          }
        }
      },
      onCellClicked: event => {
        if (!event.data) {
          return
        }
        this._view.selectedJob = event.data
        if (!this._view.selectedJob) {
          this._view.selectedJob = this.readSelectedRows()[0]
        }
      },
      onRowDoubleClicked: this._view.showDetailView4Manage,
      getRowStyle: (params) => {
        return params && params.data ? (params.data._display_rowStyle || {}) : {}
      }
    }
    this._view.columnDefs = [
      // valueGetter todo
      // { headerName: this.$t('queryJob.businessNumber'), colId: 'jobNo', field: 'jobNo', width: 180 },
      {
        headerName: this.$t('queryJob.FLCLType'),
        colId: 'containerLoadType',
        field: 'containerLoadType',
        width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return ColumnUtils.containerLoadTypeLabelFunction(params.data, 'containerLoadType')
          }
        },
        /* 设置列筛选功能的过滤器 */
        filter: false,
        filterParams: {}
      },
      { headerName: this.$t('queryJob.businessType'), colId: 'lob', field: 'lob', width: 180, cellRenderer: (params) => {
        return ColumnUtils.jobBussStateLabelFunc(params.value)
      }, enableRowGroup: true },
      // { headerName: '是否主票', colId: 'masterFlag', field: 'masterFlag', width: 180 },
      { headerName: this.$t('queryJob.mblNo'), colId: 'mbl', field: 'mbl', width: 180, enableRowGroup: true },
      { headerName: this.$t('queryJob.separateNumber'), colId: 'hblNb3', field: 'hblNb3', width: 180, enableRowGroup: true },

      // ====== 件毛体 ======
      { headerName: '实际件数', colId: 'quantityActual', field: 'quantityActual', width: 100 },
      { headerName: '实际毛重', colId: 'weightActual', field: 'weightActual', width: 100 },
      { headerName: '实际体积', colId: 'cubageActual', field: 'cubageActual', width: 100 },

      // ====== 基本页签收发通（列表显示第一行，导出保留完整数据） ======
      {
        headerName: '发货人',
        colId: 'shipperLine1',
        field: 'shipperLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          // 只取第一行用于列表显示
          return params.value.split(/\r\n|\n/)[0]
        }
      },
      {
        headerName: '收货人',
        colId: 'consigneeLine1',
        field: 'consigneeLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          return params.value.split(/\r\n|\n/)[0]
        }
      },
      {
        headerName: '通知人',
        colId: 'notifyLine1',
        field: 'notifyLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          return params.value.split(/\r\n|\n/)[0]
        }
      },

      // ====== MBL收发通（列表显示第一行，导出保留完整数据） ======
      {
        headerName: 'MBL发货人',
        colId: 'mblShipperLine1',
        field: 'mblShipperLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          return params.value.split(/\r\n|\n/)[0]
        }
      },
      {
        headerName: 'MBL收货人',
        colId: 'mblConsigneeLine1',
        field: 'mblConsigneeLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          return params.value.split(/\r\n|\n/)[0]
        }
      },
      {
        headerName: 'MBL通知人',
        colId: 'mblNotifyLine1',
        field: 'mblNotifyLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          return params.value.split(/\r\n|\n/)[0]
        }
      },

      // ====== HBL收发通（列表显示第一行，导出保留完整数据） ======
      {
        headerName: 'HBL发货人',
        colId: 'hblShipperLine1',
        field: 'hblShipperLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          return params.value.split(/\r\n|\n/)[0]
        }
      },
      {
        headerName: 'HBL收货人',
        colId: 'hblConsigneeLine1',
        field: 'hblConsigneeLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          return params.value.split(/\r\n|\n/)[0]
        }
      },
      {
        headerName: 'HBL通知人',
        colId: 'hblNotifyLine1',
        field: 'hblNotifyLine1',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return ''
          return params.value.split(/\r\n|\n/)[0]
        }
      },

      {
        headerName: this.$t('queryJob.customer'),
        colId: 'customerId',
        field: 'customerId',
        width: 180,
        valueFormatter: (params) => {
          return params.value ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(params.value), 'auto_0') : ''
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.Cargo_OperationDepartment'),
        colId: 'departmentName',
        field: 'departmentName',
        width: 180,
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('Partners.isFba'),
        colId: 'fba',
        field: 'fba',
        width: 180,
        hide: true,
        valueFormatter: (params) => {
          return ColumnUtils.booleanLabelFunction(stringToBool(params.value))
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('baseInfo.mailingAddress.fbaWarehouseCode'),
        colId: 'warehouseCode',
        field: 'warehouseCode',
        width: 180,
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('FbaJob.DestinationDistribution'),
        colId: 'destinationDistribution',
        field: 'destinationDistribution',
        width: 180,
        hide: true,
        valueFormatter: (params) => {
          return ColumnUtils.multipleFbaDestinationDistribution(TYPE_NAMES.local_cache_FbaJobConstants$DESTINATION_DISTRIBUTION_LIST, 'code', params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('FbaJob.ExpressNo'),
        colId: 'expressNo',
        field: 'expressNo',
        width: 180,
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('FbaJob.RemoteWarehouse'),
        colId: 'remoteWarehouse',
        field: 'remoteWarehouse',
        width: 180,
        hide: true,
        valueFormatter: (params) => {
          return ColumnUtils.booleanLabelFunction(stringToBool(params.value))
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('FbaJob.ShipmentId'),
        colId: 'shipmentId',
        field: 'shipmentId',
        width: 180,
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('FbaJob.ReferenceId'),
        colId: 'referenceId',
        field: 'referenceId',
        width: 180,
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.CargoType'),
        field: 'cargoType',
        colId: 'cargoType',
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_CargoType, 'code', params.value, 'chineseName')
        },
        width: 180,
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.warehouseNo'),
        colId: 'warehouseNo',
        field: 'warehouseNo',
        width: 180,
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.Cargo_sono'),
        field: 'soNo',
        colId: 'soNo',
        hide: true,
        width: 150,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.CustomerLevel'),
        colId: 'customerTypeId',
        field: 'customerTypeId',
        width: 180,
        valueGetter: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_CustomerType, 'typeId', params.data.customerTypeId, 'chineseName')
        },
        enableRowGroup: true
      },
      { headerName: this.$t('SeaExport.ContractStatus'), colId: 'contractStatus', field: 'contractStatus', width: 180, enableRowGroup: true },
      {
        headerName: this.$t('SeaExport.SalesOfGoods'),
        colId: 'cargoSalesId',
        field: 'cargoSalesId',
        width: 180,
        hide: true,
        cellRenderer: (params) => {
          return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'auto_sales')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.OverseaCustomerService'),
        colId: 'overseaOp',
        field: 'overseaOp',
        width: 180,
        hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.FieldOperation'), field: 'siteOp', width: 180, colId: 'siteOp', hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      // 承运人
      {
        headerName: this.$t('queryJob.carrier'),
        colId: 'carrierId',
        field: 'carrierId',
        width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return carrierLabelFunc(params.data, params.data.carrierId)
          }
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return carrierLabelFunc(params.data, params.data['carrierId'])
        }
      },
      {
        headerName: this.$t('queryJob.vessel'), colId: 'vessel', field: 'vessel',
        width: 180,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.ShippingCompanyContractNo'),
        colId: 'scNo',
        field: 'scNo',
        width: 180,
        hide: true,
        enableRowGroup: true
      },
      { headerName: this.$t('queryJob.voyageFlight'), colId: 'voyageFlight', field: 'voyageFlight', width: 180, enableRowGroup: true },
      // 起运港
      {
        headerName: this.$t('queryJob.portOfLoading'),
        colId: 'loadingPortCode',
        field: 'loadingPortCode',
        width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return portLabelFunc(params.data, params.data.loadingPortCode)
          }
        },
        filter: false,
        filterParams: {}
      },
      {
        headerName: this.$t('queryJob.portOfDestination'),
        colId: 'airDestinationPortCode',
        field: 'airDestinationPortCode',
        width: 180,
        cellRenderer: (params) => {
          return airDestinationPortFunction(params.data, params.value)
        },
        filter: false,
        filterParams: {}
      },
      // 交货地
      {
        headerName: this.$t('queryJob.placeOfDelivery'),
        colId: 'destinationPortCode',
        field: 'destinationPortCode',
        width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return destinationPlaceFunction(params.data, params.data.destinationPortCode)
          }
        },
        filter: false,
        filterParams: {}
      },
      // 卸货港
      {
        headerName: this.$t('queryJob.dischargingPortCode'),
        colId: 'dischargingPortCode',
        field: 'dischargingPortCode',
        width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return portLabelFunc(params.data, params.data.dischargingPortCode)
          }
        },
        filter: false,
        filterParams: {}
      },
      { headerName: this.$t('CargoMsg.Cargo_ETD'), colId: 'etd', field: 'etd', width: 180, cellRenderer: (params) => {
        return DateUtils.formatStandardDate(params.value)
      }, enableRowGroup: true },
      { headerName: this.$t('CargoMsg.Cargo_ETA'), colId: 'eta', field: 'eta', width: 180, cellRenderer: (params) => {
        return DateUtils.formatStandardDate(params.value)
      }, enableRowGroup: true },
      {
        headerName: this.$t('queryJob.businessDate'),
        colId: 'businessDate',
        field: 'businessDate',
        width: 180, cellRenderer: (params) => {
          return DateUtils.formatStandardDate(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.bookingAgent'),
        colId: 'bookingAgent',
        field: 'bookingAgent',
        width: 180, cellRenderer: (params) => {
          return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(params.value), 'auto_1')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.dateOfCreation'),
        colId: 'dateCreated',
        field: 'dateCreated',
        width: 180, valueGetter: (params) => {
          return DateUtils.formatStandardDate(params.data.dateCreated)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.dateOfProof'),
        hide: true,
        colId: 'voucherExportDate',
        field: 'voucherExportDate',
        width: 180,
        hidden: !this._view.showVoucherDate, valueGetter: (params) => {
          return DateUtils.formatStandardDate(params.data.voucherExportDate)
        },
        enableRowGroup: true
      },
      { headerName: this.$t('CargoMsg.Cargo_VoucherLetter'), field: 'prefix', hidden: !this._view.showVoucherDate, width: 80, enableRowGroup: true },
      { headerName: this.$t('CargoMsg.Cargo_VoucherNo'), field: 'documentNo', hidden: !this._view.showVoucherDate, width: 120, enableRowGroup: true },
      {
        headerName: this.$t('queryJob.businessReferenceNumber'),
        colId: 'customerRefNo',
        field: 'customerRefNo',
        width: 180,
        enableRowGroup: true
      },
      { headerName: this.$t('queryJob.itemNo'), colId: 'poNo', field: 'poNo', width: 180, enableRowGroup: true },
      // // todo-shengsheng  Desc: customJobTypeLabelFuncDiffLob
      {
        headerName: this.$t('queryJob.customJobType'),
        hide: true,
        colId: 'customJobType',
        field: 'customJobType',
        width: 180,
        valueGetter: params => {
          return ColumnUtils.resolveCutomJobTypeLabel(TYPE_NAMES.basic_data_CutomJobType, 'code', params.data.customJobType, 'name', params.data.lob, params.data.fba)
        },
        enableRowGroup: true
      },
      { headerName: this.$t('queryJob.operation'), colId: 'op', field: 'op', width: 180,
        valueGetter: (params) => {
          return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_User, 'userId', params.data.op, 'auto_op')
        }, enableRowGroup: true },
      { headerName: this.$t('queryJob.sales'), colId: 'sales', field: 'sales', width: 180,
        cellRenderer: (params) => {
          return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'auto_sales')
        }, enableRowGroup: true },
      { headerName: this.$t('queryJob.CustomerService'), colId: 'csr', field: 'csr', width: 180,
        valueGetter: (params) => {
          return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_User, 'userId', params.data.csr, 'auto_csr')
        }, enableRowGroup: true },
      { headerName: this.$t('queryJob.Documents'), colId: 'dc', field: 'dc', width: 180,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        }, enableRowGroup: true },
      {
        headerName: this.$t('queryJob.solicitationType'),
        colId: 'cargoSource',
        field: 'cargoSource',
        width: 180, cellRenderer: (params) => {
          return ColumnUtils.cargoSourceLabelFunction(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.Cargo_PrePortBusinessLeader'),
        field: 'prePortPlanner',
        colId: 'prePortPlanner',
        width: 100,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value || 0), 'chineseName')
        }
      },
      {
        headerName: this.$t('CargoMsg.Cargo_LastMileBusinessLeader'),
        field: 'lastMilePlanner',
        colId: 'lastMilePlanner',
        width: 100,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value || 0), 'chineseName')
        }
      }, {
        headerName: this.$t('CargoMsg.Cargo_preportAuditStatus'),
        field: 'prePortApproveStatus',
        colId: 'prePortApproveStatus',
        enableRowGroup: true,
        valueGetter: (params) => {
          return ColumnUtils.priceJobStateLabelFunc(params.data.prePortPlanner, Number(params.data.prePortApproveStatus))
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return ColumnUtils.priceJobStateLabelFunc(params.data.prePortPlanner, Number(params.data.prePortApproveStatus))
        },
        width: 150
      }, {
        headerName: this.$t('CargoMsg.Cargo_TrunkLineAuditStatus'),
        field: 'trunkLineApproveStatus',
        colId: 'trunkLineApproveStatus',
        enableRowGroup: true,
        valueGetter: (params) => {
          return ColumnUtils.priceJobStateLabelFunc(params.data.planner, Number(params.data.trunkLineApproveStatus))
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return ColumnUtils.priceJobStateLabelFunc(params.data.planner, Number(params.data.trunkLineApproveStatus))
        },
        width: 150
      }, {
        headerName: this.$t('CargoMsg.Cargo_TailEndAuditStatus'),
        field: 'lastMileApproveStatus',
        colId: 'lastMileApproveStatus',
        enableRowGroup: true,
        valueGetter: (params) => {
          return ColumnUtils.priceJobStateLabelFunc(params.data.lastMilePlanner, Number(params.data.lastMileApproveStatus))
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return ColumnUtils.priceJobStateLabelFunc(params.data.lastMilePlanner, Number(params.data.lastMileApproveStatus))
        },
        width: 150
      },
      {
        headerName: this.$t('queryJob.accrued'),
        hide: true,
        colId: 'provisioned',
        field: 'provisioned',
        width: 180, cellRenderer: (params) => {
          return ColumnUtils.booleanLabelFunction(stringToBool(params.value))
        },
        enableRowGroup: true
      },
      // 状态
      {
        headerName: this.$t('queryJob.status'), colId: 'jobState', field: 'jobState', width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return ColumnUtils.getJobStateForBlock(params.data)
          }
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return ColumnUtils.getJobStateForBlock(params.data)
        }
      },
      {
        headerName: this.$t('queryJob.actualChargedWeight'),
        colId: 'chargeWeight',
        field: 'chargeWeight',
        width: 180, cellRenderer: (params) => {
          return Number(ColumnUtils.formatNumber(Number(params.value), 3)).toFixed(3)
        },
        enableRowGroup: true,
        summaryMode: 'sum'
      },
      {
        headerName: this.$t('queryJob.containerNo'),
        hide: true,
        colId: 'containerNos',
        field: 'containerNos',
        width: 180, cellRenderer: (params) => {
          return ColumnUtils.getJoinContainerNo(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.profit'),
        colId: 'realProfit',
        field: 'realProfit',
        width: 180,
        cellRenderer: (params) => {
          const curValue = this._view.chargeCalculateAmount ? params.value : params.data['realProfitNoTax']
          return formatNumberToThousands(ColumnUtils.realProfit2Func(curValue))
        },
        hide: !(this._view.showRevenueColumn && this._view.showCostColumn && this._view.voucherPage !== 2),
        comparator: (a, b) => {
          if (!a) {
            a = 0
          }
          if (!b) {
            b = 0
          }
          if (a === b) {
            return 0
          } else if (a < b) {
            return -1
          } else {
            return 1
          }
        },
        summaryMode: 'sum'
      },
      {
        headerName: '应收费用利息',
        colId: 'costInterest',
        field: 'costInterest',
        width: 180,
        cellRenderer: (params) => {
          const curValue = params.value == null ? 0 : params.value
          return formatNumberToThousands(ColumnUtils.realProfit2Func(curValue))
        },
        hide: true,
        comparator: (a, b) => {
          if (!a) {
            a = 0
          }
          if (!b) {
            b = 0
          }
          if (a === b) {
            return 0
          } else if (a < b) {
            return -1
          } else {
            return 1
          }
        },
        summaryMode: 'sum'
      },
      {
        headerName: '计息利润',
        colId: 'interestProfit',
        field: 'interestProfit',
        width: 180,
        cellRenderer: (params) => {
          const curValue = this._view.chargeCalculateAmount ? params.value : params.data['interestProfitNoTax']
          return formatNumberToThousands(ColumnUtils.realProfit2Func(curValue))
        },
        hide: true,
        comparator: (a, b, item1, item2) => {
          if (!item1.data || !item2.data) {
            return 0
          }
          const actualProfit1 = this._view.chargeCalculateAmount ? Number(item1.data.interestProfit) : Number(item1.data.interestProfitNoTax)
          const actualProfit2 = this._view.chargeCalculateAmount ? Number(item2.data.interestProfit) : Number(item2.data.interestProfitNoTax)
          if (actualProfit1 === actualProfit2) {
            return 0
          } else if (actualProfit1 < actualProfit2) {
            return -1
          } else {
            return 1
          }
        },
        summaryMode: 'sum'
      },
      // 毛利率 grossProfitRateFunc
      {
        headerName: this.$t('queryJob.grossProfitRate'),
        hide: true,
        colId: 'grossProfitRate',
        field: 'grossProfitRate',
        width: 180, valueGetter: (params) => {
          if (!params.data) {
            return ''
          }
          return grossProfitPercentageFunc(params.data, this._view.chargeCalculateAmount)
        },
        comparator: (value1, value2, item1, item2) => {
          if (!item1.data || !item2.data) {
            return 0
          }
          const actualProfit1 = this._view.chargeCalculateAmount ? Number(item1.data.realProfit) : Number(item1.data.realProfitNoTax)
          const actualIncome1 = this._view.chargeCalculateAmount ? (Number(item1.data.income) || 0) : (Number(item1.data.incomeNoTax) || 0)
          const actualProfit2 = this._view.chargeCalculateAmount ? Number(item2.data.realProfit) : Number(item2.data.realProfitNoTax)
          const actualIncome2 = this._view.chargeCalculateAmount ? (Number(item2.data.income) || 0) : (Number(item2.data.incomeNoTax) || 0)
          let profit1
          if (actualIncome1 === 0) {
            profit1 = (Math.round(actualProfit1 * 100) / 100).toFixed(2)
          } else {
            profit1 = (Math.round(actualProfit1 / actualIncome1 * 10000) / 100).toFixed(2)
          }
          let profit2
          if (actualIncome2 === 0) {
            profit2 = (Math.round(actualProfit2 * 100) / 100).toFixed(2)
          } else {
            profit2 = (Math.round(actualProfit2 / actualIncome2 * 10000) / 100).toFixed(2)
          }
          return profit1 - profit2
        },
        summaryMode: 'sum'
      },
      {
        headerName: this.$t('queryJob.totalIncome'),
        colId: 'income',
        field: 'income',
        width: 180,
        cellRenderer: (params) => {
          const curValue = this._view.chargeCalculateAmount ? params.value : params.data['incomeNoTax']
          return formatNumberToThousands(ColumnUtils.formatNumber(curValue, 2, true, true))
        },
        hide: !(this._view.showRevenueColumn && this._view.voucherPage !== 2),
        comparator: (a, b) => {
          if (!a) {
            a = 0
          }
          if (!b) {
            b = 0
          }
          if (a === b) {
            return 0
          } else if (a < b) {
            return -1
          } else {
            return 1
          }
        },
        summaryMode: 'sum'
      },
      {
        headerName: this.$t('queryJob.totalCost'),
        colId: 'cost',
        field: 'cost',
        width: 180,
        cellRenderer: (params) => {
          const curValue = this._view.chargeCalculateAmount ? params.value : params.data['costNoTax']
          return formatNumberToThousands(ColumnUtils.formatNumber(curValue, 2, true, true))
        },
        comparator: (a, b) => {
          if (!a) {
            a = 0
          }
          if (!b) {
            b = 0
          }
          if (a === b) {
            return 0
          } else if (a < b) {
            return -1
          } else {
            return 1
          }
        },
        hide: !(this._view.showCostColumn && this._view.voucherPage !== 2),
        summaryMode: 'sum'
      },
      { headerName: this.$t('queryJob.NoVoucherGeneratedReceivable'), colId: 'receivableNoVoucher', field: 'receivableNoVoucher', width: 240,
        cellRenderer: (params) => {
          return formatNumberToThousands(ColumnUtils.formatNumber(Number(params.value), 2, true, true))
        }, hide: !(this._view.voucherPage === 2), enableRowGroup: true,
        summaryMode: 'sum'
      },
      { headerName: this.$t('queryJob.NoVoucherGeneratedPayable'), colId: 'payableNoVoucher', field: 'payableNoVoucher', width: 240,
        cellRenderer: (params) => {
          return formatNumberToThousands(ColumnUtils.formatNumber(Number(params.value), 2, true, true))
        }, hide: !(this._view.voucherPage === 2), enableRowGroup: true,
        summaryMode: 'sum'
      },
      { headerName: this.$t('queryJob.VoucherGeneratedReceivable'), colId: 'receivableVoucher', field: 'receivableVoucher', width: 240,
        cellRenderer: (params) => {
          return formatNumberToThousands(ColumnUtils.formatNumber(Number(params.value), 2, true, true))
        }, hide: !(this._view.voucherPage === 1), enableRowGroup: true,
        summaryMode: 'sum'
      },
      { headerName: this.$t('queryJob.VoucherGeneratedPayable'), colId: 'payableVoucher', field: 'payableVoucher', width: 240,
        cellRenderer: (params) => {
          return formatNumberToThousands(ColumnUtils.formatNumber(Number(params.value), 2, true, true))
        }, hide: !(this._view.voucherPage === 1), enableRowGroup: true,
        summaryMode: 'sum'
      },
      { headerName: this.$t('queryJob.payablesWriteOfStatus'), colId: 'due', field: 'due', width: 180, enableRowGroup: true },
      {
        headerName: this.$t('queryJob.receivablesWriteOfStatus'),
        colId: 'receivable',
        field: 'receivable',
        width: 180,
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.accrualDate'),
        hide: true,
        colId: 'provisionedDate',
        field: 'provisionedDate',
        width: 180, cellRenderer: (params) => {
          return DateUtils.formatStandardDate(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.creator'),
        colId: 'createdBy',
        field: 'createdBy',
        width: 180,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.containerDescription'),
        colId: 'containerDesc',
        field: 'containerDesc',
        width: 180,
        cellRenderer: (params) => {
          return ColumnUtils.containerDescLabelFunc(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.designationAgent'),
        colId: 'designationAgent',
        field: 'designationAgent',
        width: 180, cellRenderer: (params) => {
          return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(params.value), 'auto_3')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.masterBLFreightTerm'),
        colId: 'paymentTermMBL',
        field: 'paymentTermMBL',
        width: 180, cellRenderer: (params) => {
          return ColumnUtils.paymentTermLabelFunc(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.HBLFreightClause'),
        colId: 'paymentTermHBL',
        field: 'paymentTermHBL',
        width: 180, cellRenderer: (params) => {
          return ColumnUtils.paymentTermLabelFunc(params.value)
        },
        enableRowGroup: true
      },
      // 贸易条款
      { headerName: this.$t('queryJob.IncoTerm'), colId: 'incoTerm', field: 'incoTerm', width: 180, enableRowGroup: true },
      // 操作模式
      {
        headerName: this.$t('queryJob.OperateType'),
        colId: 'operateType',
        field: 'operateType',
        width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return ColumnUtils.operateTypeLabelFunc(params.data, 'operateType')
          }
        },
        filter: false,
        filterParams: {}
      },
      {
        headerName: this.$t('SeaExport.UserDefinedField1'),
        colId: 'userDefinedField1',
        field: 'userDefinedField1',
        width: 180,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.UserDefinedField2'),
        colId: 'userDefinedField2',
        field: 'userDefinedField2',
        width: 180,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.UserDefinedField3'),
        colId: 'userDefinedField3',
        field: 'userDefinedField3',
        width: 180,
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.ManageAuditStatus'),
        colId: 'manageAuditStatus',
        field: 'manageAuditStatus',
        width: 180, cellRenderer: (params) => {
          return ColumnUtils.manageAuditStatusLabelFunction(params.value)
        },
        enableRowGroup: true
      },
      // 航线
      {
        headerName: this.$t('queryJob.route'),
        colId: 'transportLine',
        field: 'transportLine',
        width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return ColumnUtils.routeLabelFunction(params.data)
          }
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return ColumnUtils.routeLabelFunction(params.data)
        }
      },
      {
        headerName: this.$t('SeaExport.DateOfReturningTheContainer'),
        colId: 'destPortReturnTime',
        field: 'destPortReturnTime',
        width: 180,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_MINUTE)
        },
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.DeliveryTime'),
        colId: 'destPortDeliveryTime',
        field: 'destPortDeliveryTime',
        width: 180,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_MINUTE)
        },
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.SubmissionDateTime'),
        colId: 'submissionDate',
        field: 'submissionDate',
        width: 180,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_DATE_TIME)
        },
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.AuditDateTime'),
        colId: 'fcAuditDate',
        field: 'fcAuditDate',
        width: 180,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_DATE_TIME)
        },
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.OperationDate'),
        colId: 'customsDate',
        field: 'customsDate',
        width: 180,
        cellRenderer: (params) => {
          return DateUtils.formatStandardDate(params.value)
        },
        hide: true,
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.DeliveryCompletionDate'),
        colId: 'deliveryCompletionDate',
        field: 'deliveryCompletionDate',
        width: 180,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_MINUTE)
        },
        hide: true,
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_ATD'),
        colId: 'atd',
        field: 'atd',
        width: 120,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_DATE)
        },
        hide: true,
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_ATA'),
        colId: 'ata',
        field: 'ata',
        width: 120,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_DATE)
        },
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('Invoice.invoice_receivablesInvoicingStatus'),
        colId: 'receivableInvoiceState',
        field: 'receivableInvoiceState',
        width: 180,
        cellRenderer: (params) => {
          return ColumnUtils.receivableInvoiceStateLabelFunction(params.value)
        },
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('Invoice.invoice_payablesInvoicingStatus'),
        colId: 'payableInvoiceState',
        field: 'payableInvoiceState',
        width: 180,
        cellRenderer: (params) => {
          return ColumnUtils.receivableInvoiceStateLabelFunction(params.value)
        },
        hide: true,
        enableRowGroup: true
      },
      // 约价性质
      {
        headerName: this.$t('CargoMsg.Cargo_ContractRateProperty'),
        colId: 'bookingTerm',
        field: 'bookingTerm',
        width: 180,
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            if (params.data.lob === 'SE' || params.data.lob === 'SI') {
              return ColumnUtils.bookingTermLabelFunction(params.data, params.data.bookingTerm)
            } else {
              return '-'
            }
          }
        },
        filter: false,
        filterParams: {},
        hide: true
      },
      {
        headerName: this.$t('CargoMsg.Cargo_AirlinePerson'),
        colId: 'planner',
        field: 'planner',
        width: 200,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.TEU'),
        colId: 'teu',
        field: 'teu',
        width: 200,
        hide: true,
        enableRowGroup: true,
        summaryMode: 'sum'
      }, {
        headerName: this.$t('CargoMsg.Cargo_ReceivableChargeInput'),
        field: 'receivableChargeInput',
        colId: 'receivableChargeInput',
        width: 200,
        filter: false,
        filterParams: {},
        valueGetter: (params) => {
          return ColumnUtils.chargeInputLabelFunction(params.data.receivableChargeInput)
        },
        hide: true,
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_CostChargeInput'),
        field: 'costChargeInput',
        colId: 'costChargeInput',
        width: 200,
        filter: false,
        filterParams: {},
        valueGetter: (params) => {
          return ColumnUtils.chargeInputLabelFunction(params.data.costChargeInput)
        },
        hide: true,
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.CustomsBroker'),
        field: 'customsBroker',
        colId: 'customsBroker',
        cellRenderer: (params) => {
          return params.value ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(params.value), 'auto_9') : ''
        },
        hide: true,
        width: 200,
        enableRowGroup: true
      },
      // 本地服务
      {
        headerName: this.$t('SeaExport.LocalService'),
        field: 'loadingTerm',
        colId: 'loadingTerm',
        enableRowGroup: true,
        valueGetter: (params) => {
          if (params.data) {
            return ColumnUtils.loadingTermNewLabelFunction(params.data, params.data.loadingTerm)
          }
        },
        filterValueGetter: (params) => {
          return ColumnUtils.loadingTermNewLabelFunction(params.data, params.data.loadingTerm)
        },
        filterParams: {},
        hide: true,
        width: 150
      }, {
        headerName: this.$t('queryJob.chineseDescriptionOfGoods'),
        colId: 'cargoChineseName',
        field: 'cargoChineseName',
        hide: true,
        width: 150,
        enableRowGroup: true
      }, {
        headerName: this.$t('queryJob.englishDescriptionOfGoods'),
        colId: 'cargoEnglishName',
        field: 'cargoEnglishName',
        hide: true,
        width: 150,
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.destinationAgent'),
        colId: 'destinationAgent',
        field: 'destinationAgent',
        width: 180,
        valueFormatter: (params) => {
          return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', Number(params.value), 'auto_1')
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_userDefinedRole1'),
        field: 'userDefinedRoleId1',
        colId: 'userDefinedRoleId1',
        width: 150,
        valueFormatter: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_userDefinedRole2'),
        field: 'userDefinedRoleId2',
        colId: 'userDefinedRoleId2',
        width: 150,
        valueFormatter: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      }, {
        headerName: '海外跟单',
        field: 'userDefinedRoleId4',
        colId: 'userDefinedRoleId4',
        width: 150,
        valueFormatter: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_SalesBranchCompany'),
        field: 'salesOffice',
        colId: 'salesOffice',
        hide: true,
        width: 100,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Office_All, 'officeId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_OperationOffice'),
        field: 'opOffice',
        colId: 'opOffice',
        hide: true,
        width: 100,
        valueGetter: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Office_All, 'officeId', params.data.opOffice, 'chineseName')
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_SalesDepartment'),
        field: 'salesDepartment',
        colId: 'salesDepartment',
        hide: true,
        width: 100,
        valueGetter: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Department_All, 'departmentId', params.data.salesDepartment, 'chineseName')
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.DelegatedAssociatedBusinessStatus'),
        field: 'trustJobState',
        colId: 'trustJobState',
        hide: true,
        valueFormatter: params => {
          let value = params.value
          if (typeof value === 'string') {
            value = Number(value)
            if (isNaN(value)) {
              return ''
            }
          }
          return typeof params.value === 'number' ? ColumnUtils.getJobStateCN(params.value) : ''
        },
        width: 150,
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.TransactionRelatedBusinessStatus'),
        field: 'relatedJobState',
        colId: 'relatedJobState',
        hide: true,
        valueFormatter: params => {
          let value = params.value
          if (typeof value === 'string') {
            value = Number(value)
            if (isNaN(value)) {
              return ''
            }
          }
          return typeof params.value === 'number' ? ColumnUtils.getJobStateCN(params.value) : ''
        },
        width: 150
      },
      {
        headerName: this.$t('SeaExport.PreCommitDate'),
        colId: 'preCommitDate',
        field: 'preCommitDate',
        width: 180,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_DATE_TIME)
        },
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.LockJobDate'),
        colId: 'lockJobDate',
        field: 'lockJobDate',
        width: 180,
        cellRenderer: (params) => {
          return DateUtils.customFormat(params.value, DateUtils.STANDARD_DATE_TIME)
        },
        hide: true,
        enableRowGroup: true
      },
      {
        headerName:
            this.$t('SeaExport.PreCommitUser'),
        field: 'preCommitUser',
        width: 200,
        hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.CommitUser'),
        field: 'commitUser',
        width: 200,
        hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.FcAuditUser'),
        field: 'fcAuditorUser',
        width: 200,
        hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.LockJobUser'),
        field: 'lockJobUser',
        width: 200,
        hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.MasterBusinessNo'),
        colId: 'masterJobNo',
        field: 'masterJobNo',
        width: 150,
        hide: true,
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_khPlateNumber'),
        field: 'khPlateNumber',
        colId: 'khPlateNumber',
        width: 150,
        hide: true,
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_khTrailerNumber'),
        field: 'khTrailerNumber',
        colId: 'khTrailerNumber',
        width: 150,
        hide: true,
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.ProjectName'),
        field: 'projectName',
        colId: 'projectName',
        hide: true,
        width: 150,
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_CooperateBranchId'),
        colId: 'cooperatebranch',
        field: 'cooperatebranch',
        width: 160, minWidth: 160,
        hide: !this._view.useOrderCenterMode,
        enableRowGroup: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_CooperateBusinessType'),
        colId: 'cooperateBusinessType',
        field: 'cooperateBusinessType',
        width: 200, minWidth: 200,
        hide: !this._view.useOrderCenterMode,
        enableRowGroup: true
      }, { headerName: this.$t('CargoMsg.Cargo_JobProfitShare'), colId: 'jobProfitShare', field: 'jobProfitShare', width: 130, minWidth: 130,
        cellRenderer: (params) => {
          return ColumnUtils.booleanLabelFunction(params.value)
        },
        hide: true
      }, {
        headerName: this.$t('CargoMsg.Cargo_dispatchingPlace2'),
        field: 'dispatchingPlace',
        colId: 'dispatchingPlace',
        width: 120,
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.Cargo_actTakeTime'),
        field: 'actTakeTime',
        colId: 'actTakeTime',
        width: 120,
        cellRenderer: (params) => {
          return ColumnUtils.dateTimeLabelFunc(params.value)
        },
        enableRowGroup: true
      },
      { headerName: this.$t('SeaExport.PreAuditPerson'), field: 'preverifier', width: 120, colId: 'preverifier', hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      { headerName: this.$t('SeaExport.PreAuditTime'), field: 'preverifyDate', width: 120, colId: 'preverifyDate', hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.dateLabelFunc(params.value)
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.ConsignationRelationNumber'),
        field: 'trustFromJob',
        hide: true,
        width: 150,
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.BusinessCommissionedRelationNumber'),
        field: 'relatedJob',
        hide: true,
        width: 150,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.UserDefinedField4'),
        colId: 'userDefinedField4',
        field: 'userDefinedField4',
        hide: true,
        width: 180,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.Marks'),
        field: 'mark',
        colId: 'mark',
        hide: true,
        width: 150,
        valueGetter: (params) => {
          return params.data.mark ? params.data.mark.trimEnd() : params.data.mark
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('Message.Customer_Service'),
        colId: 'csrDepartment',
        field: 'csrDepartment',
        width: 180,
        hide: true,
        valueFormatter: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Department_All, 'departmentId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('Message.Documentation_Department'),
        colId: 'dcDepartment',
        field: 'dcDepartment',
        width: 180,
        hide: true,
        valueFormatter: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Department_All, 'departmentId', Number(params.value), 'chineseName')
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.HBLTransportationClause'),
        field: 'freightTermHBL',
        colId: 'freightTermHBL',
        hide: true,
        width: 150,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.Remarks'),
        field: 'remarks',
        colId: 'remarks',
        hide: true,
        width: 150,
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.Is_Pigeonhole'),
        colId: 'pigeonhole',
        field: 'pigeonhole',
        width: 180,
        hide: true,
        valueFormatter: (params) => {
          return ColumnUtils.booleanLabelFunction(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.ARCHIVE_DATE'),
        colId: 'pigeonholeDate',
        field: 'pigeonholeDate',
        width: 180,
        hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.dateLabelFunc(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('Invoice.invoice_inputBy'),
        field: 'invoiceCreatedBy',
        width: 100,
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.CategoryOfGoodsName'),
        field: 'cargoCode',
        colId: 'cargoCode',
        cellRenderer: (params) => {
          return params.value ? ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_CargoInformation, 'code', params.value, 'chineseName') : ''
        },
        hide: true,
        width: 200,
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.ReceivableChargeLockedStatus'),
        field: 'receivableChargeLockedStatus',
        colId: 'receivableChargeLockedStatus',
        hide: true,
        width: 150,
        cellRenderer: (params) => {
          return ColumnUtils.chargeLockedStatusLabelFunc(Number(params.value))
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('SeaExport.PayableChargeLockedStatus'),
        field: 'payableChargeLockedStatus',
        colId: 'payableChargeLockedStatus',
        hide: true,
        width: 150,
        cellRenderer: (params) => {
          return ColumnUtils.chargeLockedStatusLabelFunc(Number(params.value))
        },
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.NCReferenceNo'),
        field: 'agentRefNo',
        colId: 'agentRefNo',
        hide: true,
        width: 150,
        enableRowGroup: true
      }, {
        headerName: this.$t('FinanceManage.ArRowsAndApRows'),
        field: 'arRowsAndApRows',
        colId: 'arRowsAndApRows',
        hide: true,
        width: 150,
        enableRowGroup: true
      }, {
        headerName: this.$t('FinanceManage.ArIncludeAp'),
        field: 'arIncludeAp',
        colId: 'arIncludeAp',
        hide: true,
        width: 150,
        enableRowGroup: true
      }, {
        headerName: this.$t('FinanceManage.GpExplain'),
        field: 'gpExplain',
        colId: 'gpExplain',
        hide: true,
        width: 150,
        enableRowGroup: true
      },
      {
        headerName: this.$t('FinanceManage.Allocation'), field: 'allocation', width: 100, colId: 'allocation', hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.booleanLabelFunction(stringToBool(params.value))
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('FinanceManage.Peer'), field: 'peer', width: 100, colId: 'peer', hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.booleanLabelFunction(params.value)
        }
      }, {
        headerName: this.$t('CargoMsg.Cargo_BookingNo'),
        colId: 'bookingNo',
        field: 'bookingNo',
        width: 180,
        enableRowGroup: true
      }, {
        headerName: this.$t('SeaExport.HBLHBR'),
        colId: 'jointMasterHBLJobNo',
        field: 'jointMasterHBLJobNo',
        width: 180,
        enableRowGroup: true,
        hide: true
      }, {
        headerName: this.$t('SeaExport.MBLHBR'),
        colId: 'jointMasterMBLJobNo',
        field: 'jointMasterMBLJobNo',
        width: 180,
        enableRowGroup: true,
        hide: true
      }, {
        headerName: 'MBL ' + this.$t('SeaExport.BlProperty'),
        field: 'typeMBL',
        colId: 'typeMBL',
        enableRowGroup: true,
        cellRenderer: (params) => {
          return ColumnUtils.typeMBLLabelFunc(Number(params.data.typeMBL))
        },
        hide: true,
        width: 150
      }, {
        headerName: 'HBL ' + this.$t('SeaExport.BlProperty'),
        field: 'typeHBL',
        colId: 'typeHBL',
        enableRowGroup: true,
        cellRenderer: (params) => {
          return ColumnUtils.typeHBLLabelFunc(Number(params.data.typeHBL))
        },
        hide: true,
        width: 150
      },
      {
        headerName: this.$t('SeaExport.SOPStatus'),
        field: 'sopState',
        colId: 'sopState',
        enableRowGroup: true,
        width: 100,
        tooltipValueGetter: (params) => params.value
      }, {
        headerName: this.$t('CargoMsg.Cargo_AuditCancelReason'),
        field: 'rejectReason',
        colId: 'rejectReason',
        enableRowGroup: true,
        width: 200
      },
      {
        headerName: this.$t('SeaExport.isShutOut'),
        colId: 'shutOut',
        field: 'shutOut',
        width: 180,
        hide: true,
        valueFormatter: (params) => {
          return ColumnUtils.booleanLabelFunction(stringToBool(params.value))
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('queryJob.receivablesConfirmOfStatus'),
        field: 'receivableConfirmState',
        colId: 'receivableConfirmState',
        hide: true,
        enableRowGroup: true,
        width: 200
      },
      {
        headerName: this.$t('queryJob.payablesConfirmOfStatus'),
        field: 'payableConfirmState',
        colId: 'payableConfirmState',
        hide: true,
        enableRowGroup: true,
        width: 200
      },
      { headerName: this.$t('SeaExport.BusinessLockedStatus'), field: 'businessLock', width: 100, colId: 'businessLock',
        cellRenderer: (params) => {
          return ColumnUtils.booleanLockedStatusLabelFunc(stringToBool(params.value))
        },
        hide: true,
        enableRowGroup: true
      },
      { headerName: this.$t('CargoMsg.Cargo_SettlementType'), field: 'settlementType', width: 100, hide: true, enableRowGroup: true
      },
      { headerName: this.$t('CargoMsg.Cargo_AccountPeriod'), field: 'creditDateLimited', width: 100, hide: true, enableRowGroup: true
      },
      { headerName: this.$t('CargoMsg.Cargo_IsExtended'), field: 'creditInfoDateExceeded', width: 100, hide: true, enableRowGroup: true,
        cellRenderer: params => {
          if (params.value === '') return ''
          return ColumnUtils.booleanLabelFunction(params.value)
        },
        filterParams: {},
        filterValueGetter: params => ColumnUtils.booleanLabelFunction(params.value)
      },
      { headerName: this.$t('CargoMsg.Cargo_ExcessiveOrNot'), field: 'creditInfoAmountExceeded', width: 100, hide: true, enableRowGroup: true,
        cellRenderer: params => {
          if (params.value === '') return ''
          return ColumnUtils.booleanLabelFunction(params.value)
        },
        filterParams: {},
        filterValueGetter: params => ColumnUtils.booleanLabelFunction(params.value)
      },
      {
        headerName: this.$t('CargoMsg.Cargo_CompanyDateOfCreation'),
        field: 'companyCreateDate',
        colId: 'companyCreateDate',
        enableRowGroup: true,
        hide: true,
        width: 150,
        valueFormatter: params => {
          return ColumnUtils.dateTimeLabelFunc(params.value)
        }
      },
      {
        headerName: this.$t('AirExport.EstimatedDeliveryTime'),
        field: 'estimatedTimeOfDelivery',
        colId: 'estimatedTimeOfDelivery',
        enableRowGroup: true,
        hide: true,
        width: 180,
        valueFormatter: params => {
          return ColumnUtils.dateMinutesLabelFunc(params.value)
        }
      },
      {
        headerName: this.$t('AirExport.ActualDeliveryTime'),
        field: 'actualDeliveryTime',
        colId: 'actualDeliveryTime',
        enableRowGroup: true,
        hide: true,
        width: 180,
        valueFormatter: params => {
          return ColumnUtils.dateMinutesLabelFunc(params.value)
        }
      }, {
        headerName: this.$t('CargoMsg.Cargo_InterestAmountCurrency'),
        colId: 'interestAmountCurrency',
        field: 'interestAmountCurrency',
        width: 140,
        hide: true,
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.Cargo_ReceivableFeeLockTime'), colId: 'receivableChargeLockedDate', field: 'receivableChargeLockedDate', width: 150,
        cellRenderer: (params) => {
          return DateUtils.formatStandardDateTime(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.Cargo_PayableFeeLockTime'), colId: 'payableChargeLockedDate', field: 'payableChargeLockedDate', width: 150,
        cellRenderer: (params) => {
          return DateUtils.formatStandardDateTime(params.value)
        },
        enableRowGroup: true
      },
      {
        headerName: this.$t('CargoMsg.Cargo_ContainSensitiveWord'),
        colId: 'hasSensitiveWord',
        field: 'hasSensitiveWord',
        width: 130,
        minWidth: 130,
        hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.resolveColumnLabel(TYPE_NAMES.local_cache_SystemConstants$HAS_SENSIVITE_WORD, 'code', params.value, 'chineseName')
        }
      },
      {
        headerName: this.$t('RailwayExport.departureStation'),
        colId: 'departureStation',
        field: 'departureStation',
        enableRowGroup: true,
        cellRenderer: params => {
          return params.value ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_RailwayStation, 'code', params.value, 'auto_railway') : ''
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return params.value ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_RailwayStation, 'code', params.value, 'auto_railway') : ''
        },
        width: 150
      },
      {
        headerName: this.$t('RailwayExport.transitStation'),
        colId: 'transitStation',
        field: 'transitStation',
        enableRowGroup: true,
        cellRenderer: params => {
          return params.value ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_RailwayStation, 'code', params.value, 'auto_railway') : ''
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return params.data ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_RailwayStation, 'code', params.data.transitStation, 'auto_railway') : ''
        },
        width: 150
      },
      {
        headerName: this.$t('RailwayExport.destinationStation'),
        colId: 'destinationStation',
        field: 'destinationStation',
        enableRowGroup: true,
        hide: true,
        cellRenderer: params => {
          return params.value ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_RailwayStation, 'code', params.value, 'auto_railway') : ''
        },
        filterParams: {},
        filterValueGetter: (params) => {
          return params.data ? CustomLableUtils.getLabel(TYPE_NAMES.basic_data_RailwayStation, 'code', params.data.destinationStation, 'auto_railway') : ''
        },
        width: 150
      },
      {
        headerName: this.$t('CargoMsg.Cargo_BusinessPushStatus'),
        colId: 'pushStatus',
        field: 'pushStatus',
        width: 100,
        hide: true,
        cellRenderer: (params) => {
          return ColumnUtils.jobPushStatusLabelFunc(Number(params.value))
        },
        enableRowGroup: true
      }
    ]
    await JobUtils.enrichTotalRevenueColumnDefs(this._view.columnDefs, this._view, false)
    this.optimizeColumnDefsForDisplayFields()
    cache.load(cacheName.CONFIG).then(() => {
      const jinRongExchangeMode = cache.config.get('ToExoSystem') === 'JinRongExchangeMode' // 锦荣交单模式
      if (jinRongExchangeMode) {
        this._view.columnDefs.push({
          headerName: this._view.$t('CargoMsg.Cargo_CloudPlatformSyncStatus'),
          colId: 'jobJdyxkSyncStatus',
          field: 'jobJdyxkSyncStatus',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.jobJdyxkSyncStatusLabelFunc(params.value)
          }
        })
      }
      const zy = cache.config.get('ToExoSystem') === 'zy'
      if (zy) {
        this._view.columnDefs.push({
          headerName: this._view.$t('Clearing.NewGeneration'),
          colId: 'pushExternalInterfaceStatus',
          field: 'pushExternalInterfaceStatus',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.zyInterfacePushStatusLabelFunc(params.value)
          }
        },
        {
          headerName: this.$t('CargoMsg.AllowSaleProduct'),
          field: 'saleProductCode',
          colId: 'saleProductCode',
          width: 200,
          cellRenderer: (params) => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_AllowSaleProduct, 'productCode', params.value, 'chineseName')
          }
        },
        {
          headerName: this.$t('CargoMsg.FullOrInside'),
          field: 'fullOrInside',
          colId: 'fullOrInside',
          width: 200,
          cellRenderer: (params) => {
            return ColumnUtils.fullOrInsideLabelFun(params.value)
          }
        })
      }
      const hm = cache.config.get('ToExoSystem') === 'HONGMING'
      if (hm) {
        this._view.columnDefs.push({
          headerName: this._view.$t('Clearing.PushState'),
          colId: 'hmPushExternalInterfaceStatus',
          field: 'hmPushExternalInterfaceStatus',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.zyInterfacePushStatusLabelFunc(params.value)
          }
        },
        {
          headerName: this._view.$t('Clearing.PushFailReason'),
          colId: 'pushFailReason',
          field: 'pushFailReason',
          width: 200
        })
      }
      const hc = cache.config.get('ToExoSystem') === 'HC'
      if (hc) {
        this._view.columnDefs.push({
          headerName: this._view.$t('CargoMsg.Cargo_SalesProvision'),
          colId: 'salesProvision',
          field: 'salesProvision',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_OperationTicketProvision'),
          colId: 'opTicketProvision',
          field: 'opTicketProvision',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_BusinessProvision'),
          colId: 'businessProvision',
          field: 'businessProvision',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_CustomerServiceProvision'),
          colId: 'csProvision',
          field: 'csProvision',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_OperationProvision'),
          colId: 'opProvision',
          field: 'opProvision',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_CustomsProvision'),
          colId: 'customsProvision',
          field: 'customsProvision',
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_SalesSupervisorProvision'),
          colId: 'salesSupervisorProvision',
          field: 'salesSupervisorProvision',
          hide: true,
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_OperationSupervisorProvision'),
          colId: 'opSupervisorProvision',
          field: 'opSupervisorProvision',
          hide: true,
          initialWidth: 100,
          valueFormatter: (params) => {
            return ColumnUtils.booleanLabelFunction(params.value)
          }
        })
      }
      // 专项费用录入功能配置
      const isSpecialExpenseEntryFunction = Number(cache.config.get('SpecialExpenseEntryFunction')) === 1
      if (isSpecialExpenseEntryFunction) {
        this._view.columnDefs.push({
          headerName: this._view.$t('CargoMsg.Cargo_SpecialExpensePayableTotal'),
          colId: 'specialChargeApSum',
          field: 'specialChargeApSum',
          hide: true,
          initialWidth: 100
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_SpecialExpensePayableCurrency'),
          colId: 'specialChargeApCurrency',
          field: 'specialChargeApCurrency',
          hide: true,
          initialWidth: 100
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_SpecialExpenseReceivableTotal'),
          colId: 'specialChargeArSum',
          field: 'specialChargeArSum',
          hide: true,
          initialWidth: 100
        }, {
          headerName: this._view.$t('CargoMsg.Cargo_SpecialExpenseReceivableCurrency'),
          colId: 'specialChargeArCurrency',
          field: 'specialChargeArCurrency',
          hide: true,
          initialWidth: 100
        })
      }
      this.optimizeColumnDefsForDisplayFields()
    })
    this._view.sumColumnDefs = [
      {
        field: 'chargeWeight',
        getValue: data => data.chargeWeight,
        format: value => Number(ColumnUtils.formatNumber(Number(value), 3)).toFixed(3)
      },
      {
        field: 'receivableNoVoucher',
        getValue: data => data.receivableNoVoucher,
        format: value => formatNumberToThousands(ColumnUtils.formatNumber(Number(value), 2, true, true))
      },
      {
        field: 'payableNoVoucher',
        getValue: data => data.payableNoVoucher,
        format: value => formatNumberToThousands(ColumnUtils.formatNumber(Number(value), 2, true, true))
      },
      {
        field: 'receivableVoucher',
        getValue: data => data.receivableVoucher,
        format: value => formatNumberToThousands(ColumnUtils.formatNumber(Number(value), 2, true, true))
      },
      {
        field: 'payableVoucher',
        getValue: data => data.payableVoucher,
        format: value => formatNumberToThousands(ColumnUtils.formatNumber(Number(value), 2, true, true))
      },
      {
        field: 'income',
        getValue: data => data.income,
        format: value => formatNumberToThousands(ColumnUtils.formatNumber(Number(value), 2, true, true))
      },
      {
        field: 'cost',
        getValue: data => data.cost,
        format: value => formatNumberToThousands(ColumnUtils.formatNumber(Number(value), 2, true, true))
      },
      {
        field: 'chargeWeight',
        getValue: data => data.chargeWeight,
        format: value => formatNumberToThousands(ColumnUtils.formatNumber(Number(value), 2, true, true))
      },
      {
        field: 'realProfit',
        getValue: data => data.realProfit,
        format: value => formatNumberToThousands(ColumnUtils.formatNumber(Number(value), 2, true, true))
      },
      {
        field: 'costInterest',
        getValue: data => data.costInterest,
        format: value => formatNumberToThousands(ColumnUtils.realProfit2Func(value == null ? 0 : value))
      },
      {
        field: 'interestProfit',
        getValue: data => this._view.chargeCalculateAmount ? data.interestProfit : data.interestProfitNoTax,
        format: value => formatNumberToThousands(ColumnUtils.realProfit2Func(value))
      }
    ]
  }

  // 优化列值
  optimizeColumnValue(params, column, columnVal, convertNumber = true) {
    // 进行labelFunction 处理
    if (column.cellRenderer && typeof column.cellRenderer !== 'string') {
      columnVal = column.cellRenderer(params)
    }
    // 箱号不需要去掉千分符
    if (column.field !== 'containerNo' && column.field !== 'containerNos' && columnVal && !StrUtils.isChinese(columnVal) && !StrUtils.isEnglish(columnVal) && convertNumber) {
      // 去掉千位分隔符
      let dataStr = columnVal.toString()
      dataStr = dataStr.replace(/,/g, '')
      const precision = dataStr.split('.')[1] ? dataStr.split('.')[1].length : 0
      if (!isNaN(Number(dataStr)) && precision) {
        columnVal = Number(dataStr)
      }
    }
    if (columnVal && typeof columnVal === 'string') {
      columnVal = StrUtils.replaceSpecialCharacter(columnVal)
    }
    return columnVal
  }

  // 导出excel
  async exportExcel4Grid(rows, columns, fileName) {
    const headers = []
    const excelRows = []
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const excelRow = []
      columns.forEach(column => {
        const { field, headerName } = column
        // const fieldHeader = `${field}_|_|_${headerName}`
        if (headerName || field) {
          if (i === 0) {
            headers.push(headerName)
          }
          // console.log('field', field)
          let columnVal = row.hasOwnProperty(field) ? row[field] : ''
          const params = { data: row, value: row[field] }
          columnVal = this.optimizeColumnValue(params, column, columnVal)
          excelRow.push(columnVal)
        }
      })
      excelRows.push(excelRow)
    }

    // console.log('excelRows', excelRows)

    await import('@/vendor/Export2Excel').then(excel => {
      excel.export_json_to_excel({
        header: headers,
        data: excelRows,
        filename: fileName
      })
    })
  }

  getExcelColumnFields() {
    const excelColumnDefs = [
      { field: 'jobNo', headerName: this.$t('queryJob.businessNumber'), width: 180 },
      { field: 'lob', headerName: this.$t('queryJob.businessType'), width: 180 },
      { field: 'mbl', headerName: this.$t('queryJob.mblNo'), width: 180 },
      { field: 'hblNb3', headerName: this.$t('queryJob.separateNumber'), width: 180 },
      { field: 'customerId', headerName: this.$t('queryJob.customer'), width: 180 },
      { field: 'carrierId', headerName: this.$t('queryJob.carrier'), width: 180 },
      { field: 'vessel', headerName: this.$t('queryJob.vessel'), width: 180 },
      // { field: 'scNo', headerName: 'scNo', width: 180 },
      { field: 'voyageFlight', headerName: this.$t('queryJob.voyageFlight'), width: 180 },
      { field: 'loadingPortCode', headerName: this.$t('queryJob.portOfLoading'), width: 180 },
      {
        field: 'airDestinationPortCode',
        headerName: this.$t('queryJob.portOfDestination'),
        width: 180
      },
      { field: 'destinationPortCode', headerName: this.$t('queryJob.placeOfDelivery'), width: 180 },
      {
        field: 'dischargingPortCode',
        headerName: this.$t('queryJob.dischargingPortCode'),
        width: 180
      },
      { field: 'etd', headerName: this.$t('queryJob.dateOfDeparture'), width: 180 },
      { field: 'eta', headerName: 'ETA', width: 180 },
      { field: 'businessDate', headerName: this.$t('queryJob.businessDate'), width: 180 },
      { field: 'bookingAgent', headerName: this.$t('queryJob.bookingAgent'), width: 180 },
      { field: 'dateCreated', headerName: this.$t('queryJob.dateOfCreation'), width: 180 },
      { field: 'customerRefNo', headerName: 'PO. No.', width: 180 },
      { field: 'customJobType', headerName: this.$t('queryJob.customJobType'), width: 180 },
      { field: 'op', headerName: this.$t('queryJob.operation'), width: 180 },
      { field: 'sales', headerName: this.$t('queryJob.sales'), width: 180 },
      { field: 'cargoSource', headerName: this.$t('queryJob.solicitationType'), width: 180 },
      { field: 'provisioned', headerName: this.$t('queryJob.accrued'), width: 180 },
      { field: 'jobState', headerName: this.$t('queryJob.status'), width: 180 },
      { field: 'chargeWeight', headerName: this.$t('queryJob.actualChargedWeight'), width: 180 },
      { field: 'containerNos', headerName: this.$t('queryJob.containerNo'), width: 180 },
      { field: 'realProfit', headerName: this.$t('queryJob.profit'), width: 180 },
      { field: 'costInterest', headerName: '应收费用利息', width: 180 },
      { field: 'interestProfit', headerName: '计息利润', width: 180 },
      { field: 'grossProfitRate', headerName: this.$t('queryJob.grossProfitRate'), width: 180 },
      { field: 'income', headerName: this.$t('queryJob.totalIncome'), width: 180 },
      { field: 'cost', headerName: this.$t('queryJob.totalCost'), width: 180 },
      { field: 'due', headerName: this.$t('queryJob.payablesWriteOfStatus'), width: 180 },
      { field: 'receivable', headerName: this.$t('queryJob.receivablesWriteOfStatus'), width: 180 },
      { field: 'provisionedDate', headerName: this.$t('queryJob.accrualDate'), width: 180 },
      { field: 'createdBy', headerName: this.$t('queryJob.creator'), width: 180 },
      { field: 'containerDesc', headerName: this.$t('queryJob.containerDescription'), width: 180 },
      { field: 'designationAgent', headerName: this.$t('queryJob.designationAgent'), width: 180 },
      { field: 'paymentTermMBL', headerName: this.$t('queryJob.masterBLFreightTerm'), width: 180 },
      { field: 'paymentTermHBL', headerName: this.$t('queryJob.HBLFreightClause'), width: 180 },
      { field: 'destPortReturnTime', headerName: this.$t('SeaExport.DateOfReturningTheContainer'), width: 180 },
      { field: 'destPortDeliveryTime', headerName: this.$t('SeaExport.ReturnTime'), width: 180 },
      { field: 'customsDate', headerName: this.$t('SeaExport.OperationDate'), width: 180 }
    ]

    return excelColumnDefs
  }

  // 列表上方的打印按钮
  printClickHandler(command) {
    const uids = []
    const list = getGridSelected(this._view.gridOptions)
    for (const job of list) {
      uids.push(job.uuid)
    }
    if (uids.length > 0) {
      // TemplateSelectorWin.getInstance().showThisWin(_job, "JOBLIST", {"JOB_UUID":uids}, financeView);
      this._view.templateSelectorDialog.category = 'JOBLIST'
      this._view.templateSelectorDialog.data = {
        'JOB_UUID': uids,
        'printSource': DocumentConstants.BATCH_PRINT_CWSH.code
      }
      // 是否按照多个打印
      if (uids.length > 1 && command === this.$t('queryJob.PrintByBusinessNumber')) {
        Object.assign(this._view.templateSelectorDialog.data, {
          'JOB_UUID': uids[0],
          multiplePrint: true,
          multipleJobUuid: uids
        })
      }
      this._view.templateSelectorDialog.visible = true
    } else {
      this.$message.warning(this.$t('Clearing.PleaseBusinessYouWantPrint'))
    }
  }

  // 批量更新汇率
  batchUpdateExchangeRate() {
    const list = getGridSelected(this._view.gridOptions)
    if (!list || list.length === 0) {
      this.$message.warning(this.$t('Message.SelectJob'))
      return
    }
    const keys = []
    for (const item of list) {
      // 财务归档生效判断
      const warnflag = ArchiveEffectUtils.checkArchiveEffect(item)
      if (warnflag) {
        this.$message.warning(this._view.$t('CargoMsg.NOT_OPERATIONAL'))
        return
      }
      if (cache.config.get('ToExoSystem') === 'WK' && item.pushStatus > 0) {
        this.$message.warning(this._view.$t('CargoMsg.Cargo_CancelApprovalCheck'))
        return
      }
      if (item.uuid) {
        keys.push(item.uuid)
      }
    }
    this._view.updateExchangeRateDialog.jobKeyList = keys
    this._view.updateExchangeRateDialog.visible = true
  }

  // CARGO-36429，财务审核新增关闭功能
  // 归档
  async pigeonholeHandler() {
    const list = getGridSelected(this._view.gridOptions)
    if (!list || list.length === 0) {
      this.$message.warning(this.$t('Message.SelectJob'))
      return
    }
    for (const job of list) {
      if (job.pigeonhole) {
        this.$message.warning(this.$t('CargoMsg.ONLY_NOT_ARCHIVED_TRANSACTIONS'))
        return
      }
    }
    let flag = false
    // 二次确认
    await this.$confirm(this.$t('CargoMsg.Confirm_Archiving_Business'), this.$t('CargoMsg.Cargo_Tips'), { type: 'warning' }).then(() => {
      flag = true
    }).catch(() => { flag = false })
    if (!flag) {
      return
    }
    this.loading()
    const isZh = this.$store.getters.language === 'zh'
    // 查询当前分公司配置的归档设置
    let archivingConfig = {}
    await getArchivingConfig(this.$store.getters.currentOfficeId).then(res => {
      if (res && res.data) {
        archivingConfig = res.data
      }
    }).catch(() => {
      this.closeLoading()
    })
    const uuids = list.map((row, index, arr) => {
      return row[`uuid`]
    })
    let changingChargeList = []
    // 查询更改费用审核中有无进行中的数据
    // 状态：0=待提交、1=待判责审核、2=待商务审核、3=待关账判定、4=已生效、5=已拒绝、6=已撤销申请、7=待经理审核
    await queryChangingChargeList({ jobUuids: uuids, states: [0, 1, 2, 3, 6, 7] }).then(res => {
      if (res.data && res.data.length > 0) {
        changingChargeList = res.data
      }
    }).catch(() => {
      this.closeLoading()
    })

    list.forEach(job => {
      let canPigeonholeFalg = true
      // 审核状态
      if (job.jobState === SystemConstants.VERIFIED) {
        // 已审核
        job.financialReviewStatus = FinanceInterfaceConstants.VOUCHER_AUDITED.code
      } else {
        // 未审核
        job.financialReviewStatus = FinanceInterfaceConstants.VOUCHER_UNAUDITED.code
        if (archivingConfig.financialAlertType === SystemConstants.STRONG_VERIFICATION.code) {
          canPigeonholeFalg = false
        }
      }
      // 其他提醒
      const otherReminders = []
      // 是否已计提
      if (archivingConfig.accruedAlertType !== SystemConstants.NO_REMINDERS.code && !job.provisioned) {
        // 设置提醒
        otherReminders.push(this.$t('CargoMsg.Not_Accrued'))
        // 设置了强校验
        if (archivingConfig.accruedAlertType === SystemConstants.STRONG_VERIFICATION.code) {
          canPigeonholeFalg = false
        }
      }
      // 是否应收全部核销
      if (archivingConfig.receivablesVerificationAlertType !== SystemConstants.NO_REMINDERS.code) {
        if (isZh) {
          // job.receivable 后端返回的具体字符串，前端用字符串匹配
          // 应收核销状态等于 未核销或者部分核销 添加提醒
          if (job.receivable === SystemConstants.NOT_AllVERIFICATION.chineseName || job.receivable === SystemConstants.PARTS_VERIFICATION.chineseName) {
            // 设置提醒
            otherReminders.push(this.$t('CargoMsg.Receivables_Not_Fully_Written_Off'))
            // 设置了强校验
            if (archivingConfig.receivablesVerificationAlertType === SystemConstants.STRONG_VERIFICATION.code) {
              canPigeonholeFalg = false
            }
          }
        } else {
          // 应收核销状态等于 未核销或者部分核销 添加提醒
          if (job.receivable === SystemConstants.NOT_AllVERIFICATION.englishName || job.receivable === SystemConstants.PARTS_VERIFICATION.englishName) {
            // 设置提醒
            otherReminders.push(this.$t('CargoMsg.Receivables_Not_Fully_Written_Off'))
            // 设置了强校验
            if (archivingConfig.receivablesVerificationAlertType === SystemConstants.STRONG_VERIFICATION.code) {
              canPigeonholeFalg = false
            }
          }
        }
      }
      // 是否应付全部核销
      if (archivingConfig.dueVerificationAlertType !== SystemConstants.NO_REMINDERS.code) {
        if (isZh) {
          // job.due 后端返回的具体字符串，前端用字符串匹配
          // 应付核销状态等于 未核销或者部分核销 添加提醒
          if (job.due === SystemConstants.NOT_AllVERIFICATION.chineseName || job.due === SystemConstants.PARTS_VERIFICATION.chineseName) {
            // 设置提醒
            otherReminders.push(this.$t('CargoMsg.Payable_Not_Fully_Written_Off'))
            // 设置了强校验
            if (archivingConfig.dueVerificationAlertType === SystemConstants.STRONG_VERIFICATION.code) {
              canPigeonholeFalg = false
            }
          }
        } else {
          // 应付核销状态等于 未核销或者部分核销 添加提醒
          if (job.due === SystemConstants.NOT_AllVERIFICATION.englishName || job.due === SystemConstants.PARTS_VERIFICATION.englishName) {
            // 设置提醒
            otherReminders.push(this.$t('CargoMsg.Payable_Not_Fully_Written_Off'))
            // 设置了强校验
            if (archivingConfig.dueVerificationAlertType === SystemConstants.STRONG_VERIFICATION.code) {
              canPigeonholeFalg = false
            }
          }
        }
      }

      // 是否有进行中的费用新增/更改/删除 有申请在审核流程中。未完全批准，或未被退回
      if (archivingConfig.chargeAlertType === SystemConstants.STRONG_VERIFICATION.code && changingChargeList && changingChargeList.length > 0) {
        for (const changingCharge of changingChargeList) {
          if (changingCharge.jobUuid === job.uuid) {
            canPigeonholeFalg = false
            otherReminders.push(this.$t('CargoMsg.CHANGE_CHARGE_IN_PROGRESS'))
            break
          }
        }
      }

      if (otherReminders.length > 0) {
        job.otherReminders = otherReminders.join(',')
      }
      // 是否可归档
      if (canPigeonholeFalg) {
        job.isCanPigeonhole = SystemConstants.ARCHIVEABLE.code
      } else {
        job.isCanPigeonhole = SystemConstants.NOT_ARCHIVEABLE.code
      }
    })

    // 归档弹框
    this.financeBtnGroupView.pigeonholeDialog.visible = true
    this.financeBtnGroupView.pigeonholeDialog.data = list
    this.closeLoading()
  }
  pigeonhole() {
    const list = getGridSelected(this._view.gridOptions)
    const ids = list.map((row, index, arr) => {
      return row[`uuid`]
    })
    const loading = getLoading()
    // 归档
    updateJobPigeonholeByJobUuid(ids, 1).then(res => {
      loading.close()
      if (res.data > 0) {
        this.$message.success(this.$t('Message.SaveSuccessfully'))
        this.refresh()
      }
    }).catch(() => {
      loading.close()
    }).finally(() => {
      this.financeBtnGroupView.pigeonholeDialog.visible = false
    })
  }
  // 解除归档
  async unarchiveHandler() {
    const list = getGridSelected(this._view.gridOptions)
    if (!list || list.length === 0) {
      this.$message.warning(this.$t('Message.SelectJob'))
      return
    }
    for (const job of list) {
      if (!job.pigeonhole) {
        this.$message.warning(this.$t('CargoMsg.ONLY_ARCHIVED_TRANSACTIONS'))
        return
      }
    }
    let flag = false
    // 二次确认
    await this.$confirm(this.$t('CargoMsg.Confirm_Unarchiving_Business'), this.$t('CargoMsg.Cargo_Tips'), { type: 'warning' }).then(() => {
      flag = true
    }).catch(() => {
      flag = false
    })
    if (!flag) {
      return
    }
    const ids = list.map((row, index, arr) => {
      return row[`uuid`]
    })
    const loading = getLoading()
    // 解除归档接口
    updateJobPigeonholeByJobUuid(ids, 0).then(res => {
      if (res.data > 0) {
        loading.close()
        this.$message.success(this.$t('Message.OperationSuccessfully'))
        this.refresh()
      }
    }).catch(() => {
      loading.close()
    })
  }
}

// 港口
function portLabelFunc(data, value) {
  if (!value) return ''
  if (data && value && Object.prototype.hasOwnProperty.call(data, 'lob') && (data.lob === 'SI' || data.lob === 'SE')) {
    return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Seaport, 'code', value, 'auto_7')
  } else if (data && value && Object.prototype.hasOwnProperty.call(data, 'lob') && (data.lob === 'AI' || data.lob === 'AE')) {
    return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Airport, 'code', value, 'auto_8')
  }
  return ''
}
// 承运人c
function carrierLabelFunc(data, value) {
  if (!value) return ''
  if (data && value && Object.prototype.hasOwnProperty.call(data, 'lob') && (data.lob === 'SI' || data.lob === 'SE')) {
    return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', value, 'auto_5')
  } else if (data && value && Object.prototype.hasOwnProperty.call(data, 'lob') && (data.lob === 'AI' || data.lob === 'AE')) {
    return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', value, 'auto_6')
  } else if (data && value && Object.prototype.hasOwnProperty.call(data, 'lob') && (data.lob === 'IE')) {
    // 卡车公司
    return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', value, 'auto_11')
  }
  return ''
}

/**
 * 只显示空运业务的目的港
 */
function airDestinationPortFunction(data, value) {
  if (!value) value = data['destinationPortCode']
  if (data && value && Object.prototype.hasOwnProperty.call(data, 'lob') && (data.lob === 'AI' || data.lob === 'AE')) {
    return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Airport, 'code', value, 'auto_8')
  }
  return ''
}

/**
 * 交货地：海运出口显示destinationPortCode，其他显示destinationPlace
 */
function destinationPlaceFunction(data, value) {
  if (data && Object.prototype.hasOwnProperty.call(data, 'lob') && data.lob === 'SE') {
    return CustomLableUtils.getLabel(TYPE_NAMES.basic_data_Seaport, 'code', value, 'auto_7')
  }
  if (data && Object.prototype.hasOwnProperty.call(data, 'lob') && (data.lob === 'AI' || data.lob === 'SI')) {
    return data['destinationPlace']
  }
  return ''
}

// 毛利率 利润/收入
function grossProfitRateFunc(item) {
  const realProfit = item.realProfit
  const income = item.income
  const result = '0.00'
  if (isNaN(realProfit) || Number(realProfit) === 0) {
    return result
  } else if (isNaN(income) || Number(income) === 0) {
    return (Math.round(realProfit * 100) / 100).toFixed(2)
  } else {
    return (Math.round(realProfit / income * 100) / 100).toFixed(2)
  }
}

// 毛利率 利润/收入 百分比形式
function grossProfitPercentageFunc(item, chargeCalculateAmount) {
  const actualProfit = chargeCalculateAmount ? Number(item.realProfit) : Number(item.realProfitNoTax)
  const actualIncome = chargeCalculateAmount ? (Number(item.income) || 0) : (Number(item.incomeNoTax) || 0)
  if (actualIncome === 0) {
    return (Math.round(actualProfit * 100) / 100).toFixed(2)
  } else {
    return (Math.round(actualProfit / actualIncome * 10000) / 100).toFixed(2) + '%'
  }
}

// 贸易条款
// function incoTermToColumnFunction(incoTerm) {
//   return incoTerm || ''
// }

// 航线：铁路航线与其他四大业务
function routeLabelFunction(data) {
  if (data && data.hasOwnProperty('lob')) {
    if (data.lob === 'RE' || data.lob === 'RI') {
      const cacheList = ColumnUtils.getBasicDataCacheList(basicCacheKeys.basic_data_RailwayLine, (item) => {
        if (item.code === data['transportLine']) {
          return true
        }
      })
      if (cacheList && cacheList.length) {
        return cacheList[0].name
      }
    } else if (data.lob === 'SE' || data.lob === 'AE') {
      return data['transportLine']
    }
  }
  return ''
}
// ===========================================================备份==============================================================
// this._view.columnDefs = [
//   // valueGetter todo
//   // { headerName: this.$t('queryJob.businessNumber'), field: 'jobNo', width: 180 },
//   { headerName: this.$t('queryJob.businessType'), field: 'lob',
//     cellRenderer: (params) => {
//       return ColumnUtils.jobBussStateLabelFunc(params.value)
//     },
//     width: 180 },
//   { headerName: '是否主票', field: 'masterFlag', width: 180 },
//   { headerName: this.$t('queryJob.mblNo'), field: 'mbl', width: 180 },
//   { headerName: this.$t('queryJob.separateNumber'), field: 'hblNb3', width: 180 },
//   { headerName: this.$t('queryJob.customer'), field: 'customerId',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('SeaExport.CustomerLevel'), field: 'customerTypeId',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_CustomerType, 'typeId', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('SeaExport.ContractStatus'), field: 'contractStatus', width: 180 },
//   { headerName: this.$t('SeaExport.SalesOfGoods'), field: 'cargoSalesId',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('SeaExport.OverseaCustomerService'), field: 'overseaOp',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.carrier'), field: 'carrierId',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.vessel'), field: 'vessel',
//     width: 180 },
//   { headerName: this.$t('SeaExport.ShippingCompanyContractNo'), field: 'scNo', width: 180 },
//   { headerName: this.$t('queryJob.voyageFlight'), field: 'voyageFlight', width: 180 },
//   { headerName: this.$t('queryJob.portOfLoading'), field: 'loadingPortCode',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Seaport, 'code', params.value, 'englishName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.portOfDestination'), field: 'destinationPortCode',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Seaport, 'code', params.value, 'englishName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.placeOfDelivery'), field: 'destinationPortCode',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Seaport, 'code', params.value, 'englishName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.dischargingPortCode'), field: 'dischargingPortCode',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Seaport, 'code', params.value, 'englishName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.dateOfDeparture'), field: 'ETD', width: 180 },
//   { headerName: this.$t('queryJob.businessDate'), field: 'businessDate', width: 180 },
//   { headerName: this.$t('queryJob.bookingAgent'), field: 'bookingAgent',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.dateOfCreation'), field: 'dateCreated', width: 180 },
//   { headerName: this.$t('queryJob.dateOfProof'), field: 'voucherExportDate', width: 180 },
//   { headerName: this.$t('queryJob.businessReferenceNumber'), field: 'customerRefNo', width: 180 },
//   { headerName: this.$t('queryJob.itemNo'), field: 'PONO', width: 180 },
//   { headerName: this.$t('queryJob.customJobType'), field: 'customJobType', width: 180 },
//   { headerName: this.$t('queryJob.operation'), field: 'op',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.sales'), field: 'sales',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.CustomerService'), field: 'csr',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.Documents'), field: 'dc',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.solicitationType'), field: 'cargoSource',
//     cellRenderer: (params) => {
//       return ColumnUtils.cargoSourceLabelFunction(params.value)
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.accrued'), field: 'isProvisioned',
//     cellRenderer: (params) => {
//       return ColumnUtils.booleanLabelFunction(params.value)
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.status'), field: 'jobState',
//     cellRenderer: (params) => {
//       return ColumnUtils.jobStateLabelFunc(params.value)
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.actualChargedWeight'), field: 'chargeWeight',
//     cellRenderer: (params) => {
//       return ColumnUtils.number1ColumnLabelFunc(params.value)
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.containerNo'), field: 'containerNo', width: 180 },
//   { headerName: this.$t('queryJob.profit'), field: 'realProfit',
//     cellRenderer: (params) => {
//       return ColumnUtils.realProfitFunc(params.value)
//     },
//     width: 180 },
//   // 毛利率
//   { headerName: this.$t('queryJob.grossProfitRate'), field: 'grossProfitRate', width: 180 },
//   { headerName: this.$t('queryJob.totalIncome'), field: 'income',
//     cellRenderer: (params) => {
//       return ColumnUtils.number2ColumnLabelFunc(params.value)
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.totalCost'), field: 'cost',
//     cellRenderer: (params) => {
//       return ColumnUtils.number2ColumnLabelFunc(params.value)
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.payablesWriteOfStatus'), field: 'due', width: 180 },
//   { headerName: this.$t('queryJob.receivablesWriteOfStatus'), field: 'receivable', width: 180 },
//   { headerName: this.$t('queryJob.accrualDate'), field: 'provisionedDate', width: 180 },
//   { headerName: this.$t('queryJob.creator'), field: 'createdBy',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.containerDescription'), field: 'containerDesc',
//     cellRenderer: (params) => {
//       return ColumnUtils.containerDescLabelFunc(params.value)
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.designationAgent'), field: 'designationAgent',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', params.value, 'chineseName')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.masterBLFreightTerm'), field: 'paymentTermMBL',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_PaymentTerm, 'code', params.value, 'name')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.HBLFreightClause'), field: 'paymentTermHBL',
//     cellRenderer: (params) => {
//       return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_PaymentTerm, 'code', params.value, 'name')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.IncoTerm'), field: 'incoTerm', width: 180 },
//   { headerName: this.$t('queryJob.FLCLType'), field: 'containerLoadType',
//     cellRenderer: (params) => {
//       // console.log('params.data containerLoadType', params.data)
//       return ColumnUtils.containerLoadTypeLabelFunction(params.data, 'containerLoadType')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.OperateType'), field: 'operateType',
//     cellRenderer: (params) => {
//       return ColumnUtils.operateTypeLabelFunc(params.data, 'operateType')
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.UserDefinedField1'), field: 'userDefinedField1', width: 180 },
//   { headerName: this.$t('queryJob.UserDefinedField2'), field: 'userDefinedField2', width: 180 },
//   { headerName: this.$t('queryJob.UserDefinedField3'), field: 'userDefinedField3', width: 180 },
//   { headerName: this.$t('queryJob.ManageAuditStatus'), field: 'manageAuditStatus',
//     cellRenderer: (params) => {
//       return ColumnUtils.manageAuditStatusLabelFunction(params.value)
//     },
//     width: 180 },
//   { headerName: this.$t('queryJob.route'), field: 'transportLine', width: 180 }
// ]
